// src/pages/ChatPage.tsx
import React, { useState, useEffect, useRef, ChangeEvent, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  HeadsetMic as HeadsetMicIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Reply as ReplyIcon,
  Comment as CommentIcon,
  Event as EventIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  AttachFile as AttachFileIcon,
  SmartToy as SmartToyIcon,
  HelpOutline as HelpOutlineIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  Public as PublicIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { api } from '../services/api';
import { Chat, Message, MessageType, ChatType, ActivityReservationMetadata, ServiceTaskMetadata, GuestCommentMetadata } from '../types/chat';
import DOMPurify from 'dompurify';

/** Best-effort parse of CommentID from GSC create/update response shapes. */
function parsePmsGuestCommentId(data: unknown): number | undefined {
  if (data == null) return undefined;
  if (typeof data === 'number' && Number.isFinite(data)) return data;
  if (typeof data === 'string' && /^\d+$/.test(data.trim())) return parseInt(data.trim(), 10);
  if (typeof data === 'object') {
    const o = data as Record<string, unknown>;
    for (const key of ['CommentID', 'commentID', 'commentId', 'Data']) {
      const c = o[key];
      if (typeof c === 'number' && Number.isFinite(c)) return c;
      if (typeof c === 'string' && /^\d+$/.test(c.trim())) return parseInt(c.trim(), 10);
    }
  }
  return undefined;
}

function resolveCommentIdFromGuestProfile(
  profilePayload: any,
  target: { commentText?: string; commentTypeID?: string; status?: string },
): number | undefined {
  const pools = [
    profilePayload?.guestComments,
    profilePayload?.GuestComments,
    profilePayload?.comments,
    profilePayload?.Comments,
    profilePayload?.Data?.guestComments,
    profilePayload?.Data?.GuestComments,
  ].filter(Array.isArray);

  const rows = (pools.flat() as any[]).filter(Boolean);
  if (!rows.length) return undefined;

  const normalizedText = String(target.commentText || '').trim().toLowerCase();
  const normalizedType = String(target.commentTypeID || '').trim();
  const normalizedStatus = String(target.status || '').trim().toLowerCase();

  const bestMatch = rows.find((row) => {
    const rowText = String(row.CommentText || row.commentText || '').trim().toLowerCase();
    const rowType = String(row.CommentTypeID || row.commentTypeID || row.commentTypeId || '').trim();
    const rowStatus = String(row.Status || row.status || '').trim().toLowerCase();
    return rowText === normalizedText && rowType === normalizedType && rowStatus === normalizedStatus;
  });

  if (bestMatch) {
    return parsePmsGuestCommentId(bestMatch);
  }

  // Fallback: same text + type (status may differ after workflows)
  const softMatch = rows.find((row) => {
    const rowText = String(row.CommentText || row.commentText || '').trim().toLowerCase();
    const rowType = String(row.CommentTypeID || row.commentTypeID || row.commentTypeId || '').trim();
    return rowText === normalizedText && rowType === normalizedType;
  });

  return parsePmsGuestCommentId(softMatch);
}

// Types for configuration data – API'den gelen tüm tipler listelenir (InternalType filtresi yok)
interface ActivityType {
  InternalType: string;
  ID: string;
  Code: string;
  Description: string;
  SessionMandatory: boolean;
  /** Weekday names when activity is available (e.g. ["Monday","Tuesday"]). Empty = every day. */
  ActiveDays?: string[];
  Sessions?: ActivitySession[];
}

interface ActivitySession {
  ID: string;
  Code: string;
  Description: string;
  Time: string;
  Direction?: string | null;
}

function getNormalizedSessionDirection(direction?: string | null): 'to' | 'from' | 'unknown' {
  if (!direction) return 'unknown';
  const normalized = direction.toLowerCase().replace(/[^a-z]/g, '');
  if (normalized.includes('todestination') || normalized.includes('todestionation')) {
    return 'to';
  }
  if (normalized.includes('fromdestination') || normalized.includes('fromdestionation')) {
    return 'from';
  }
  return 'unknown';
}

function getFlagEmoji(countryCode?: string | null): string {
  if (!countryCode) return '';
  const code = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    127397 + code.charCodeAt(0),
    127397 + code.charCodeAt(1),
  );
}

const InlineFlagIcon: React.FC<{ countryCode: string; size?: number }> = ({ countryCode, size = 13 }) => {
  const cc = countryCode.toLowerCase();
  return (
    <Box
      component="img"
      src={`https://flagcdn.com/w${Math.max(20, size * 2)}/${cc}.png`}
      alt={countryCode}
      sx={{
        width: size * 1.3,
        height: size,
        objectFit: 'cover',
        borderRadius: '2px',
        border: '0.5px solid rgba(0, 0, 0, 0.15)',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        flexShrink: 0,
      }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        target.src = `https://flagcdn.com/${cc}.svg`;
      }}
    />
  );
};

function maskPhone(value?: string | null): string {
  if (!value) return '';
  const chars = value.split('');
  const digitPositions = chars
    .map((ch, idx) => (/\d/.test(ch) ? idx : -1))
    .filter((idx) => idx >= 0);

  const keepFrom = Math.max(0, digitPositions.length - 4);
  const keepSet = new Set(digitPositions.slice(keepFrom));

  return chars
    .map((ch, idx) => (/\d/.test(ch) && !keepSet.has(idx) ? '*' : ch))
    .join('');
}

function isUUIDLike(value?: string | null): boolean {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

/** Weekday names as returned by API (ActiveDays). getDay() is 0=Sun..6=Sat. */
const ACTIVITY_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
function getDayNameForDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return ACTIVITY_DAY_NAMES[d.getDay()];
}

interface ServiceTaskConfig {
  Types?: Array<{
    ID: string;
    Code: string;
    Description: string;
    DisplayOrder?: number;
    Inactive?: boolean;
    GroupID?: string;
    Group?: {
      ID: string;
      Code: string;
      Description: string;
      DisplayOrder?: number;
      Inactive?: boolean;
      Department?: {
        ID: string;
        Code: string;
        Description: string;
      };
    };
  }>;
  Departments?: Array<{
    Role: string;
    ID: string;
    Code: string;
    Description: string;
    DisplayOrder: number;
    Inactive: boolean;
  }>;
  Groups?: Array<{
    Department: {
      Role: string;
      ID: string;
      Code: string;
      Description: string;
      DisplayOrder: number;
      Inactive: boolean;
    };
    ID: string;
    Code: string;
    Description: string;
    DisplayOrder: number;
    Inactive: boolean;
  }>;
  Localities: Array<{
    Type: string;
    ID: string;
    Description: string;
  }>;
  StatusLevels: Array<{
    ID: string;
    Description: string;
  }>;
  Origins: Array<{
    ID: string;
    Description: string;
  }>;
}

interface GuestCommentConfig {
  Types: Array<{
    ID: string;
    Code: string;
    Description: string;
    Group?: {
      ID: string;
      Code: string;
      Description: string;
      Department?: {
        ID: string;
        Code: string;
        Description: string;
      };
    };
  }>;
  Groups: Array<{
    ID: string;
    Code: string;
    Description: string;
    Department?: {
      ID: string;
      Code: string;
      Description: string;
    };
  }>;
  Departments: Array<{
    ID: string;
    Code: string;
    Description: string;
  }>;
}

type DialogSeverity = 'info' | 'success' | 'warning' | 'error';

interface InfoDialogState {
  open: boolean;
  title: string;
  message: string;
  severity: DialogSeverity;
}

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
}

// Activity Reservation Card Component
const ActivityReservationCard: React.FC<{
  message: Message,
  onEdit: (message: Message) => void,
  onCancel: (message: Message) => void,
  onConfirm?: (message: Message) => void,
  isOperator?: boolean,
  t: (key: string) => string,
}> = ({ message, onEdit, onCancel, onConfirm, isOperator = false, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const metadata = message.metadata as ActivityReservationMetadata;
  const isCancelled = metadata?.status === 'cancelled';
  const isConfirmed = metadata?.status === 'confirmed';

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <Box
      sx={{
        bgcolor: isDark ? (isCancelled ? 'rgba(15,23,42,0.7)' : 'rgba(15,23,42,0.85)') : (isCancelled ? '#f8fafc' : '#ffffff'),
        border: `1px solid ${isDark ? 'rgba(148,163,184,0.2)' : '#e2e8f0'}`,
        borderRadius: 8,
        p: 2.5,
        maxWidth: '320px',
        opacity: isCancelled ? 0.6 : 1,
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Avatar sx={{
          bgcolor: isDark ? '#f472b6' : '#ec4899',
          width: 32,
          height: 32,
        }}>
          <EventIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1f2937' }}>
          {metadata?.guestName || t('chat.guest')}
        </Typography>
      </Box>

      {/* Activity Details */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <EventIcon sx={{ fontSize: 18, color: isDark ? '#f9a8d4' : '#f093fb' }} />
          <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#374151', fontWeight: 600 }}>
            {metadata?.activityName || t('chat.activity')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ fontSize: 18 }}>📅</Box>
          <Typography variant="body2" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 500 }}>
            {formatDate(metadata?.date || '')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: 18 }}>🕐</Box>
          <Typography variant="body2" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 500 }}>
            {metadata?.session || t('chat.session')}
          </Typography>
        </Box>
      </Box>

      {/* Status Badge */}
      {isCancelled && (
        <Chip
          label={t('chat.cancelled')}
          size="small"
          sx={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: '24px',
            mb: 1.5,
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
          }}
        />
      )}
      {!isCancelled && isConfirmed && (
        <Chip
          label={t('chat.confirmed')}
          size="small"
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: '24px',
            mb: 1.5,
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
          }}
        />
      )}

      {/* Action Buttons */}
      {!isCancelled && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexDirection: 'column' }}>
          {/* Show confirm button only if operator and activity not yet confirmed */}
          {isOperator && onConfirm && !isConfirmed && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onConfirm(message)}
              sx={{
                width: '100%',
                bgcolor: '#10b981',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 1,
                                borderRadius: 16,
                '&:hover': {
                  bgcolor: '#059669',
                  boxShadow: 'none',
                },
              }}
            >
              {t('chat.confirm')}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onCancel(message)}
              sx={{
                flex: 1,
                borderColor: '#e2e8f0',
                color: isDark ? '#cbd5f5' : '#64748b',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                                borderRadius: 16,
                bgcolor: isDark ? 'rgba(15,23,42,0.6)' : '#ffffff',
                '&:hover': {
                  borderColor: '#ef4444',
                  bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
                },
              }}
            >
              {t('login.cancel')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => onEdit(message)}
              sx={{
                flex: 1,
                bgcolor: '#667eea',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                                borderRadius: 16,
                '&:hover': {
                  bgcolor: '#5568d3',
                  boxShadow: 'none',
                },
              }}
            >
              {t('chat.edit')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Guest Comment Card Component
const GuestCommentCard: React.FC<{
  message: Message,
  t: (key: string) => string,
  onGuestCommentReply: (message: Message, replyNote: string) => Promise<void>,
}> = ({ message, t, onGuestCommentReply }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const metadata = message.metadata as GuestCommentMetadata;
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  const commentTypeID =
    metadata.commentTypeID ??
    (metadata.commentTypeId != null ? String(metadata.commentTypeId) : '');
  const canReply = Boolean(
    (metadata.commentId != null || metadata.pendingCommentId) &&
      metadata.profileId &&
      metadata.reservationId &&
      commentTypeID,
  );

  const openReply = () => {
    setReplyDraft(metadata.replyNote ?? '');
    setReplyOpen(true);
  };

  const submitReply = async () => {
    const trimmed = replyDraft.trim();
    if (!trimmed) return;
    try {
      setReplySubmitting(true);
      await onGuestCommentReply(message, trimmed);
      setReplyOpen(false);
    } catch {
      /* error surfaced via parent setError */
    } finally {
      setReplySubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Positive':
        return { bg: isDark ? 'rgba(16,185,129,0.15)' : '#d1fae5', color: isDark ? '#bbf7d0' : '#065f46', emoji: '😊' };
      case 'Negative':
        return { bg: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: isDark ? '#fecaca' : '#991b1b', emoji: '😞' };
      default:
        return { bg: isDark ? 'rgba(148,163,184,0.2)' : '#f3f4f6', color: isDark ? '#e2e8f0' : '#374151', emoji: '😐' };
    }
  };

  const statusStyle = getStatusColor(metadata?.status || 'Neutral');

  return (
    <Box
      sx={{
        bgcolor: isDark ? 'rgba(15,23,42,0.85)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(148,163,184,0.2)' : '#e2e8f0'}`,
        borderRadius: 8,
        p: 2.5,
        maxWidth: '400px',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Avatar sx={{
          bgcolor: isDark ? '#c4b5fd' : '#8b5cf6',
          width: 32,
          height: 32,
        }}>
          <CommentIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1f2937' }}>
          {t('chat.guestCommentSaved')}
        </Typography>
      </Box>

      {/* Guest Name */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {t('chat.guest')}
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#1f2937', fontWeight: 500 }}>
          {metadata?.guestName || t('chat.unknown')}
        </Typography>
      </Box>

      {/* Sentiment */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {t('chat.commentStatus')}
        </Typography>
        <Chip
          label={`${statusStyle.emoji} ${metadata?.status || 'Neutral'}`}
          size="small"
          sx={{
            bgcolor: statusStyle.bg,
            color: statusStyle.color,
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
          }}
        />
      </Box>

      {/* Comment Text */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {t('chat.commentText')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: isDark ? '#e2e8f0' : '#374151',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6,
          }}
        >
          {metadata?.commentText || ''}
        </Typography>
      </Box>

      {/* Staff reply (PMS ReplyNote) */}
      {metadata?.replyNote ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {t('chat.guestCommentStaffReply')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#e2e8f0' : '#374151',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              bgcolor: isDark ? 'rgba(59,130,246,0.12)' : 'rgba(219,234,254,0.6)',
              borderRadius: 1,
              px: 1.25,
              py: 1,
            }}
          >
            {metadata.replyNote}
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        {canReply ? (
          <Button
            size="small"
            startIcon={<ReplyIcon sx={{ fontSize: 18 }} />}
            onClick={openReply}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {metadata?.replyNote ? t('chat.guestCommentEditReply') : t('chat.guestCommentReply')}
          </Button>
        ) : (
          <Tooltip title={t('chat.guestCommentCannotReplyHint')} arrow>
            <span>
              <Button
                size="small"
                startIcon={<ReplyIcon sx={{ fontSize: 18 }} />}
                disabled
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('chat.guestCommentReply')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Dialog open={replyOpen} onClose={() => !replySubmitting && setReplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('chat.guestCommentReplyTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('chat.guestCommentReplyNote')}
            placeholder={t('chat.guestCommentReplyPlaceholder')}
            multiline
            minRows={3}
            fullWidth
            value={replyDraft}
            onChange={(e) => setReplyDraft(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setReplyOpen(false)} disabled={replySubmitting} sx={{ textTransform: 'none' }}>
            {t('login.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitReply()}
            disabled={replySubmitting || !replyDraft.trim()}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {replySubmitting ? t('chat.sending') : t('chat.guestCommentReplySubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Footer */}
      <Box sx={{
        mt: 2,
        pt: 1.5,
        borderTop: `1px solid ${isDark ? 'rgba(148,163,184,0.2)' : '#e2e8f0'}`,
      }}>
        <Typography variant="caption" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontStyle: 'italic' }}>
          ℹ️ {t('chat.guestCommentSavedInfo')}
        </Typography>
      </Box>
    </Box>
  );
};

// Service Task Card Component
const ServiceTaskCard: React.FC<{
  message: Message,
  onEdit: (message: Message) => void,
  onCancel: (message: Message) => void,
  onConfirm?: (message: Message) => void,
  isOperator: boolean,
  canGuestCancel?: boolean,
  t: (key: string) => string,
}> = ({ message, onEdit, onCancel, onConfirm, isOperator, canGuestCancel, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const metadata = message.metadata as ServiceTaskMetadata;
  const isCancelled = metadata?.status === 'cancelled';
  const isCompleted = metadata?.status === 'completed';

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return '#ef4444';
      case 'Low': return '#10b981';
      default: return '#f59e0b';
    }
  };

  const getPriorityEmoji = (priority: string) => {
    switch (priority) {
      case 'High': return '🔴';
      case 'Low': return '🟢';
      default: return '🟡';
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <Box
      sx={{
        bgcolor: isDark
          ? (isCancelled || isCompleted ? 'rgba(15,23,42,0.6)' : 'rgba(15,23,42,0.85)')
          : (isCancelled || isCompleted ? '#f8fafc' : '#ffffff'),
        border: `1px solid ${isDark ? 'rgba(148,163,184,0.2)' : '#e2e8f0'}`,
        borderRadius: 8,
        p: 2.5,
        maxWidth: '350px',
        opacity: isCancelled || isCompleted ? 0.6 : 1,
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Avatar sx={{
          bgcolor: isDark ? '#60a5fa' : '#3b82f6',
          width: 32,
          height: 32,
        }}>
          <BuildIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1f2937' }}>
          {metadata?.guestName || t('chat.guest')}
        </Typography>
      </Box>

      {/* Task Details */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <BuildIcon sx={{ fontSize: 18, color: isDark ? '#93c5fd' : '#3b82f6' }} />
          <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#374151', fontWeight: 600 }}>
            {t('chat.serviceTask')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ fontSize: 18 }}>📍</Box>
          <Typography variant="body2" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 500 }}>
            {metadata?.localityType} - {metadata?.locality}
          </Typography>
        </Box>

        {/* Only show priority to operators/admins */}
        {isOperator && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ fontSize: 18 }}>{getPriorityEmoji(metadata?.priority || 'Normal')}</Box>
            <Typography variant="body2" sx={{ color: isDark ? '#fef3c7' : getPriorityColor(metadata?.priority || 'Normal'), fontWeight: 700 }}>
              {metadata?.priority || t('chat.normal')} {t('chat.priority')}
            </Typography>
          </Box>
        )}

        {metadata?.dueDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ fontSize: 18 }}>📅</Box>
            <Typography variant="body2" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 500 }}>
              {t('chat.due')}: {formatDate(metadata.dueDate)}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5 }}>
          <Box sx={{ fontSize: 18 }}>📝</Box>
          <Typography variant="body2" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', flex: 1, fontWeight: 500 }}>
            {metadata?.notes || t('chat.noDescription')}
          </Typography>
        </Box>
      </Box>

      {/* Linked Image */}
      {metadata?.imageUrl && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="caption" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 600, display: 'block', mb: 1 }}>
            📷 {t('chat.attachedImage')}:
          </Typography>
          <img
            src={normalizeImageUrl(metadata.imageUrl)}
            alt="Linked to task"
            style={{
              maxWidth: '100%',
              maxHeight: 150,
              borderRadius: 16,
              cursor: 'pointer',
              objectFit: 'contain',
              border: `1px solid ${isDark ? 'rgba(148,163,184,0.2)' : 'rgba(0, 0, 0, 0.08)'}`,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
            onClick={() => {
              // Open image in new tab
              if (metadata.imageUrl) {
                window.open(normalizeImageUrl(metadata.imageUrl), '_blank');
              }
            }}
          />
        </Box>
      )}

      {/* Status Badge */}
      {isCancelled && (
        <Chip
          label={t('chat.cancelled')}
          size="small"
          sx={{
            bgcolor: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2',
            color: isDark ? '#fecaca' : '#991b1b',
            fontWeight: 500,
            fontSize: '0.75rem',
            height: '24px',
            mb: 1.5,
          }}
        />
      )}
      {isCompleted && (
        <Chip
          label={t('chat.completed')}
          size="small"
          sx={{
            bgcolor: isDark ? 'rgba(16,185,129,0.15)' : '#d1fae5',
            color: isDark ? '#bbf7d0' : '#065f46',
            fontWeight: 500,
            fontSize: '0.75rem',
            height: '24px',
            mb: 1.5,
          }}
        />
      )}

      {canGuestCancel && !isCancelled && !isCompleted && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={() => onCancel(message)}
            sx={{
              borderColor: '#ef4444',
              color: '#ef4444',
              textTransform: 'none',
              borderRadius: 16,
            }}
          >
            {t('chat.cancelRequest')}
          </Button>
        </Box>
      )}

      {/* Action Buttons - Only for operators/admins */}
      {isOperator && !isCancelled && !isCompleted && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexDirection: 'column' }}>
          {onConfirm && metadata?.status === 'pending' && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onConfirm(message)}
              sx={{
                width: '100%',
                bgcolor: '#10b981',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 1,
                                borderRadius: 16,
                '&:hover': {
                  bgcolor: '#059669',
                  boxShadow: 'none',
                },
              }}
            >
              {t('chat.confirm')}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onCancel(message)}
              sx={{
                flex: 1,
                borderColor: isDark ? 'rgba(148,163,184,0.3)' : '#e2e8f0',
                color: isDark ? '#cbd5f5' : '#64748b',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                                borderRadius: 16,
                bgcolor: isDark ? 'rgba(15,23,42,0.6)' : '#ffffff',
                '&:hover': {
                  borderColor: '#ef4444',
                  bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
                },
              }}
            >
              {t('login.cancel')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => onEdit(message)}
              sx={{
                flex: 1,
                bgcolor: '#3b82f6',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                                borderRadius: 16,
                '&:hover': {
                  bgcolor: '#2563eb',
                  boxShadow: 'none',
                },
              }}
            >
              {t('chat.edit')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Helper function to normalize image URLs to always use /api/uploads/ path
const normalizeImageUrl = (url: string): string => {
  if (!url) return url;

  // If it's already a full URL with /api/uploads/, return as is
  if (url.includes('/api/uploads/')) {
    return url;
  }

  // If it's a relative path starting with /uploads/, convert to /api/uploads/
  if (url.startsWith('/uploads/')) {
    return url.replace(/^\/uploads\//, '/api/uploads/');
  }

  // If it's a full URL without /api/, try to insert /api before /uploads/
  if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
    return url.replace(/\/uploads\//, '/api/uploads/');
  }

  // If it's just a filename or relative path, construct the full path
  if (!url.startsWith('http') && !url.startsWith('/')) {
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || window.location.origin;
    return `${baseUrl}/api/uploads/chat-images/${url}`;
  }

  return url;
};

// Helper component to render a single message bubble
const MessageBubble: React.FC<{
  message: Message,
  isOwn: boolean,
  getMessageAvatar: (m: Message) => string | React.ReactNode,
  isOperator: boolean,
  onAction: (action: string, message: Message) => void,
  onEditActivity: (message: Message) => void,
  onCancelActivity: (message: Message) => void,
  onConfirmActivity?: (message: Message) => void,
  onEditServiceTask: (message: Message) => void,
  onCancelServiceTask: (message: Message) => void,
  onConfirmServiceTask?: (message: Message) => void,
  onGuestCommentReply: (message: Message, replyNote: string) => Promise<void>,
  onImageClick: (imageUrl: string) => void,
  currentUserId?: string,
  currentProfileId?: string,
  isGuestUser?: boolean,
  t: (key: string) => string,
}> = ({ message, isOwn, getMessageAvatar, isOperator, onAction, onEditActivity, onCancelActivity, onConfirmActivity, onEditServiceTask, onCancelServiceTask, onConfirmServiceTask, onGuestCommentReply, onImageClick, currentUserId, currentProfileId, isGuestUser, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isLLM = message.isFromLLM;
  const isSystem = message.type === MessageType.SYSTEM;
  const isImage = message.type === MessageType.IMAGE;
  const isActivityReservation = message.type === MessageType.ACTIVITY_RESERVATION;
  const isServiceTask = message.type === MessageType.SERVICE_TASK;
  const isGuestComment = isSystem && message.metadata && 'guestName' in message.metadata && 'commentText' in message.metadata && 'status' in message.metadata;
  const [isHovering, setIsHovering] = useState(false);

  const senderRole = message.sender?.role;

  const bubbleStyle = (() => {
    if (isLLM) {
      return {
        background: isDark
          ? 'linear-gradient(180deg, rgba(99,164,245,0.35), rgba(78,147,239,0.25))'
          : 'linear-gradient(180deg,#63A4F5,#4E93EF)',
        color: isDark ? '#dbeafe' : '#ffffff',
        border: 'none',
        shadow: isDark ? '0 3px 10px rgba(24,155,240,.25)' : '0 3px 10px rgba(24,155,240,.18)',
      };
    }
    if (isOwn || senderRole === 'guest') {
      return {
        background: isDark ? 'rgba(30, 41, 59, 0.95)' : '#ffffff',
        color: isDark ? '#e2e8f0' : '#28324A',
        border: isDark ? '1px solid rgba(148, 163, 184, 0.25)' : 'none',
        shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(21,41,77,.08)',
      };
    }
    return {
      background: isDark ? 'rgba(5, 150, 105, 0.15)' : '#ffffff',
      color: isDark ? '#bbf7d0' : '#28324A',
      border: isDark ? '1px solid rgba(16, 185, 129, 0.4)' : 'none',
      shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(21,41,77,.08)',
    };
  })();

  const textColor = bubbleStyle.color;

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: string) => {
    onAction(action, message);
    handleMenuClose();
  };

  // Render guest comment card - only visible to operators and admins
  if (isGuestComment && isOperator) {
    return (
      <ListItem
        key={message.id}
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
        }}
      >
        <GuestCommentCard
          message={message}
          t={t}
          onGuestCommentReply={onGuestCommentReply}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: '0.65rem' }}>
          {new Date(message.createdAt).toLocaleTimeString()}
        </Typography>
      </ListItem>
    );
  }

  // If it's a guest comment but user is not operator/admin, don't render anything (or render as regular system message)
  if (isGuestComment && !isOperator) {
    return null;
  }

  if (isSystem) {
    // Filter out welcome and operator assignment messages
    const isWelcomeOrOperatorMessage = message.content.includes('Welcome to Room')
      || (message.content.includes('Welcome to') && message.content.includes('includes all guests'))
      || (
        message.content.includes('Operator')
        && message.content.includes('assigned')
        && message.content.includes('includes all guests')
      )
      || message.content.includes('This chat includes all guests');

    if (isWelcomeOrOperatorMessage) {
      return null;
    }

    // Guests must not see AI on/off status chips (operator/admin only)
    const isAiStatusSystemMessage =
      message.content === 'AI responses enabled' ||
      message.content === 'AI responses disabled' ||
      /^AI Assistant disabled - .+ is now assisting$/.test(message.content) ||
      message.content === t('chat.aiResponsesEnabled') ||
      message.content === t('chat.aiResponsesDisabled') ||
      message.content === t('chat.aiAssistantEnabled') ||
      message.content === t('chat.aiAssistantDisabled') ||
      /^AI Asistan(ı)? devre dışı/.test(message.content) ||
      /^AI Asistan etkinleştirildi/.test(message.content) ||
      /^AI yanıtları (devre dışı|etkin)/.test(message.content);

    if (isGuestUser && isAiStatusSystemMessage) {
      return null;
    }

    // Translate system messages based on content patterns
    const translateSystemMessage = (content: string): string => {
      // AI responses enabled/disabled
      if (content === 'AI responses enabled') {
        return t('chat.aiResponsesEnabled');
      }
      if (content === 'AI responses disabled') {
        return t('chat.aiResponsesDisabled');
      }

      // AI Assistant disabled with operator name
      const aiDisabledMatch = content.match(/^AI Assistant disabled - (.+) is now assisting$/);
      if (aiDisabledMatch) {
        const translated = t('chat.aiAssistantDisabledOperator');
        return translated.replace('{operatorName}', aiDisabledMatch[1]);
      }

      // "{Name} has joined this chat"
      const joinedMatch = content.match(/^(.+) has joined this chat$/i);
      if (joinedMatch) {
        return t('chat.hasJoinedChat').replace('{name}', joinedMatch[1]);
      }

      // Welcome personal chat message
      // Match: "Welcome to your personal chat, {firstName}! You can ask me anything here. An operator can also join this chat if you need human assistance."
      const welcomeMatch = content.match(/^Welcome to your personal chat, (.+)! You can ask me anything here\. An operator can also join this chat if you need human assistance\.$/);
      if (welcomeMatch) {
        const translated = t('chat.welcomePersonalChat');
        return translated.replace('{firstName}', welcomeMatch[1]);
      }

      // Also handle case where the message might have different punctuation or spacing
      const welcomeMatchAlt = content.match(/^Welcome to your personal chat, (.+)! You can ask me anything here/);
      if (welcomeMatchAlt && content.includes('operator can also join')) {
        const translated = t('chat.welcomePersonalChat');
        return translated.replace('{firstName}', welcomeMatchAlt[1]);
      }

      // Fallback to original content if no match
      return content;
    };

    return (
      <Box sx={{ textAlign: 'center', py: 0.75, display: 'flex', justifyContent: 'center' }}>
        <Chip
          label={translateSystemMessage(message.content)}
          size="small"
          sx={{
            bgcolor: isDark ? 'rgba(30, 41, 59, 0.9)' : '#fff',
            color: isDark ? theme.palette.text.secondary : '#6B7488',
            fontWeight: 600,
            fontSize: '12px',
            border: `1px solid ${isDark ? 'rgba(148,163,184,0.3)' : '#E4E9F1'}`,
            borderRadius: 999,
            px: 1,
            height: 'auto',
            py: 0.75,
            maxWidth: '94%',
            boxShadow: isDark ? 'none' : '0 1px 3px rgba(21,41,77,.05)',
            '& .MuiChip-label': {
              whiteSpace: 'normal',
              textAlign: 'center',
            },
          }}
        />
      </Box>
    );
  }

  // Render activity reservation card
  if (isActivityReservation) {
    return (
      <ListItem
        key={message.id}
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
        }}
      >
        <ActivityReservationCard
          message={message}
          onEdit={onEditActivity}
          onCancel={onCancelActivity}
          onConfirm={onConfirmActivity}
          isOperator={isOperator}
          t={t}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: '0.65rem' }}>
          {new Date(message.createdAt).toLocaleTimeString()}
        </Typography>
      </ListItem>
    );
  }

  // Render service task card
  if (isServiceTask) {
    return (
      <ListItem
        key={message.id}
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
        }}
      >
        <ServiceTaskCard
          message={message}
          onEdit={onEditServiceTask}
          onCancel={onCancelServiceTask}
          onConfirm={onConfirmServiceTask}
          isOperator={isOperator}
          canGuestCancel={(() => {
            const stMeta = message.metadata as ServiceTaskMetadata;
            if (!isGuestUser || stMeta?.status !== 'pending') return false;
            if (!stMeta?.requestedByUserId && !stMeta?.guestProfileId) return true;
            if (stMeta.requestedByUserId && stMeta.requestedByUserId === currentUserId) return true;
            if (
              currentProfileId &&
              stMeta.guestProfileId &&
              String(stMeta.guestProfileId) === String(currentProfileId)
            ) {
              return true;
            }
            return false;
          })()}
          t={t}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: '0.65rem' }}>
          {new Date(message.createdAt).toLocaleTimeString()}
        </Typography>
      </ListItem>
    );
  }

  return (
    <ListItem
      key={message.id}
      sx={{
        flexDirection: isLLM ? 'row' : (isOwn ? 'row-reverse' : 'row'),
        alignItems: 'flex-start',
        justifyContent: isLLM ? 'flex-start' : (isOwn ? 'flex-end' : 'flex-start'),
        py: 1.5,
        position: 'relative',
        display: 'flex',
        width: '100%',
        ml: isLLM ? 0 : (isOwn ? 'auto' : 0),
        mr: isLLM ? 0 : (isOwn ? 0 : 'auto'),
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Avatar */}
      <Tooltip title={isLLM ? t('chat.aiAssistant') : message.sender?.firstName || t('chat.unknown')}>
        <Avatar
          src={!isLLM && message.sender?.profilePicture ? message.sender.profilePicture : undefined}
          sx={{
            background: isLLM
              ? '#189BF0'
              : isOwn
                ? 'linear-gradient(135deg, #B37BF0 0%, #9B5CE0 100%)'
                : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
            mx: 1,
            width: 38,
            height: 38,
            mt: 0.25,
            fontSize: '0.875rem',
            boxShadow: isLLM ? 'none' : '0 2px 6px rgba(21,41,77,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getMessageAvatar(message)}
        </Avatar>
      </Tooltip>

      {/* Message Content Box and Action Icons Container */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: 'relative',
        flex: 1,
        justifyContent: isLLM ? 'flex-start' : (isOwn ? 'flex-end' : 'flex-start'),
      }}>
        {/* Message Content Box */}
        <Box
          sx={{
            bgcolor: bubbleStyle.background,
            background: bubbleStyle.background,
            color: bubbleStyle.color,
            p: '15px 16px 9px',
            borderRadius: isLLM || !isOwn
              ? '18px 18px 18px 6px'
              : '18px 18px 6px 18px',
            maxWidth: '88%',
            boxShadow: bubbleStyle.shadow,
            wordBreak: 'break-word',
            minWidth: 120,
            position: 'relative',
            border: bubbleStyle.border,
            transition: 'all 0.2s ease',
          }}
        >
          {/* Reply indicator */}
          {message.replyTo && (
            <Box
              sx={{
                bgcolor: 'rgba(102, 126, 234, 0.08)',
                p: 1.5,
                mb: 1.5,
                borderLeft: '3px solid',
                borderColor: '#667eea',
                                borderRadius: 16,
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea' }}>
                {message.replyTo.sender?.firstName || t('chat.unknown')}
              </Typography>
              <Typography variant="caption" display="block" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#6b7280',
                mt: 0.5,
              }}>
                {message.replyTo.content.substring(0, 50)}
                {message.replyTo.content.length > 50 ? '...' : ''}
              </Typography>
            </Box>
          )}

          {/* Display image if it's an image message */}
          {isImage && (
            <Box sx={{
              mb: 1,
              backgroundColor: isDark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 16,
              p: 1,
              display: 'inline-block',
            }}>
              <img
                src={normalizeImageUrl(message.content)}
                alt=""
                onClick={() => onImageClick(normalizeImageUrl(message.content))}
                style={{
                  maxWidth: '100%',
                  borderRadius: 16,
                  maxHeight: '200px',
                  objectFit: 'contain',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: 'none',
                  transition: 'all 0.3s ease',
                  display: 'block',
                }}
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none';
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              />
            </Box>
          )}

          {/* Display text content only if it's NOT an image message AND there's actual text content */}
          {!isImage && message.content && message.content.trim() && (
            <>
              {/* Render HTML for LLM responses */}
              {isLLM ? (
                <Box
                  className="llm-content" // CSS seçimi için sınıf ekledik
                  sx={{
                    color: textColor,
                    // Temel HTML elementleri için stil sıfırlama ve yapılandırma
                    '& p': { marginBottom: '0.5rem', lineHeight: 1.5 },
                    '& ul, & ol': { paddingLeft: '1.5rem', marginBottom: '1rem' },
                    '& li': { marginBottom: '0.25rem' },
                    '& strong': { fontWeight: 600 },

                    // Bootstrap benzeri kart yapıları için özel stiller
                    '& .card': {
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      borderRadius: '12px',
                      padding: '12px',
                      marginBottom: '12px',
                      backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.7)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    },

                    // Alert kutuları için stiller
                    '& .alert': {
                      padding: '10px 15px',
                      borderRadius: '10px',
                      marginBottom: '12px',
                      fontSize: '0.9rem',
                      borderLeft: '4px solid',
                    },
                    '& .alert-info': {
                      backgroundColor: isDark ? 'rgba(14, 165, 233, 0.15)' : '#e0f2fe',
                      color: isDark ? '#38bdf8' : '#0369a1',
                      borderLeftColor: '#0ea5e9',
                    },
                    '& .alert-success': {
                      backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : '#dcfce7',
                      color: isDark ? '#4ade80' : '#15803d',
                      borderLeftColor: '#22c55e',
                    },

                    // Tablo stilleri
                    '& .table-responsive': {
                      overflowX: 'auto',
                      marginBottom: '1rem',
                    },
                    '& .table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.875rem',
                    },
                    '& .table th': {
                      textAlign: 'left',
                      padding: '8px',
                      borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                      fontWeight: 600,
                    },
                    '& .table td': {
                      padding: '8px',
                      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`,
                    }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(message.content, {
                      // İzin verilen tag listesini genişletelim
                      ALLOWED_TAGS: [
                        'div', 'p', 'span', 'br', 'strong', 'em', 'u', 'b', 'i',
                        'table', 'thead', 'tbody', 'tr', 'th', 'td',
                        'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                        'hr', 'code', 'pre'
                      ],
                      ALLOWED_ATTR: ['class', 'style', 'className'], // className'i de ekleyin
                    }),
                  }}
                />
              ) : (
                /* Regular text for non-LLM messages */
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    color: textColor,
                  }}
                >
                  {message.content}
                </Typography>
              )}
            </>
          )}

          {/* Timestamp and Status */}
          <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>
              {/* Show sender name if not own message */}
              {!isOwn && (message.sender?.firstName || t('chat.unknownStaff')) + ' • '}

              {new Date(message.createdAt).toLocaleTimeString()}

              {/* Optimistic Update/Sent Status */}
              {isOwn && message.id.startsWith('temp-') && (
                <Tooltip title={t('chat.sending')}>
                  <CircularProgress size={12} sx={{ ml: 0.5, opacity: 0.7, color: '#667eea' }} />
                </Tooltip>
              )}
              {isOwn && !message.id.startsWith('temp-') && (
                <Tooltip title={t('chat.sent')}>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 16,
                      ml: 0.5,
                      color: isDark ? '#34d399' : '#15803d',
                      filter: isDark ? 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' : 'none'
                    }}
                  />
                </Tooltip>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Action icons - show only for operators on hover for guest messages only (not AI) */}
        {isOperator && !isSystem && !isOwn && !isLLM && message.sender?.role === 'guest' && isHovering && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Tooltip title={t('chat.reply')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuAction('reply');
                }}
                sx={{
                  width: 32,
                  height: 32,
                                borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    borderColor: '#cbd5e1',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ReplyIcon sx={{ fontSize: 16, color: '#64748b', transform: 'scaleX(-1)' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('chat.newActivityReservation')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuAction('activity');
                }}
                sx={{
                  width: 32,
                  height: 32,
                                borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    borderColor: '#cbd5e1',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <LocationIcon sx={{ fontSize: 16, color: '#64748b' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('chat.makeGuestComment')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuAction('comment');
                }}
                sx={{
                  width: 32,
                  height: 32,
                                borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    borderColor: '#cbd5e1',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <CommentIcon sx={{ fontSize: 16, color: '#64748b' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('chat.newServiceTask')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuAction('service');
                }}
                sx={{
                  width: 32,
                  height: 32,
                                borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#ef4444',
                  '&:hover': {
                    bgcolor: '#dc2626',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <BuildIcon sx={{ fontSize: 16, color: '#ffffff' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 6,
            mt: 1,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(20px)',
            '& .MuiMenuItem-root': {
                                borderRadius: 16,
              mx: 1,
              my: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                transform: 'translateX(4px)',
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => handleMenuAction('reply')}>
          <ReplyIcon sx={{ mr: 1.5, fontSize: 18, color: '#667eea' }} />
          <Typography sx={{ fontWeight: 500, color: '#1f2937' }}>{t('chat.reply')}</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('comment')}>
          <CommentIcon sx={{ mr: 1.5, fontSize: 18, color: '#667eea' }} />
          <Typography sx={{ fontWeight: 500, color: '#1f2937' }}>{t('chat.makeGuestComment')}</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('activity')}>
          <EventIcon sx={{ mr: 1.5, fontSize: 18, color: '#f093fb' }} />
          <Typography sx={{ fontWeight: 500, color: '#1f2937' }}>{t('chat.newActivityReservation')}</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('service')}>
          <BuildIcon sx={{ mr: 1.5, fontSize: 18, color: '#3b82f6' }} />
          <Typography sx={{ fontWeight: 500, color: '#1f2937' }}>{t('chat.newServiceTask')}</Typography>
        </MenuItem>
      </Menu>
    </ListItem>
  );
};

const ChatPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { connection, joinChat, sendMessage, isConnected } = useSocket();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [chat, setChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLLMTyping, setIsLLMTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isAIEnabled, setIsAIEnabled] = useState(true); // AI toggle state
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [hotelSettings, setHotelSettings] = useState<any>(null); // Hotel settings for night mode

  // AI Text Suggestion states
  const [textSuggestion, setTextSuggestion] = useState<string | null>(null);
  const [isFetchingSuggestion, setIsFetchingSuggestion] = useState(false);
  const suggestionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSentMessageRef = useRef<string>(''); // Track last sent message to prevent suggestions for already sent messages

  // AI Service Note Enhancement states
  const [isEnhancingServiceNote, setIsEnhancingServiceNote] = useState(false);

  // Modal states
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [serviceTaskModalOpen, setServiceTaskModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  // Form states for Guest Comment
  const [commentDepartment, setCommentDepartment] = useState<string>('');
  const [commentGroup, setCommentGroup] = useState<string>('');
  const [commentType, setCommentType] = useState<string>('');
  const [commentText, setCommentText] = useState('');
  const [commentStatus, setCommentStatus] = useState('Positive');

  // Form states for Activity Reservation
  const [activityName, setActivityName] = useState('');
  const [activityEventName, setActivityEventName] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [activitySession, setActivitySession] = useState('');
  const [shuttleOutboundSession, setShuttleOutboundSession] = useState('');
  const [shuttleReturnSession, setShuttleReturnSession] = useState('');
  const [activityTypeId, setActivityTypeId] = useState<string>('');
  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | null>(null);

  // Form states for Service Task
  const [serviceTaskDepartment, setServiceTaskDepartment] = useState<string>('');
  const [serviceTaskGroup, setServiceTaskGroup] = useState<string>('');
  const [serviceTaskType, setServiceTaskType] = useState<string>('');
  const [serviceLocalityType, setServiceLocalityType] = useState('Room');
  const [serviceLocality, setServiceLocality] = useState('');
  const [servicePriority, setServicePriority] = useState('Normal');
  const [serviceNotes, setServiceNotes] = useState('');
  const [serviceDueDate, setServiceDueDate] = useState('');
  const [selectedTaskImage, setSelectedTaskImage] = useState<Message | null>(null);

  // Configuration data from GSC API
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [serviceTaskConfig, setServiceTaskConfig] = useState<ServiceTaskConfig | null>(null);
  const [guestCommentConfig, setGuestCommentConfig] = useState<GuestCommentConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [detailedGuestProfile, setDetailedGuestProfile] = useState<any | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const confirmDialogResolverRef = useRef<((value: boolean) => void) | null>(null);

  const [infoDialog, setInfoDialog] = useState<InfoDialogState>({
    open: false,
    title: '',
    message: '',
    severity: 'info',
  });

  const [confirmDialogState, setConfirmDialogState] = useState<ConfirmDialogState>({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Onayla',
    cancelLabel: 'İptal',
  });

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
                        borderRadius: 6,
      backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
      '&:hover': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? 'rgba(255,255,255,0.3)' : '#cbd5e1',
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? '#3b82f6' : '#667eea',
          borderWidth: 2,
        },
      },
      '& fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: isDark ? theme.palette.text.secondary : '#64748b',
      '&.Mui-focused': {
        color: isDark ? '#3b82f6' : '#667eea',
        fontWeight: 500,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
  };

  const closeInfoDialog = () => {
    setInfoDialog((prev) => ({ ...prev, open: false }));
  };

  const showInfoDialog = useCallback(
    (message: string, options?: { title?: string; severity?: DialogSeverity }) => {
      setInfoDialog({
        open: true,
        title: options?.title || 'Bilgilendirme',
        message,
        severity: options?.severity || 'info',
      });
    },
    [],
  );

  const requestConfirmation = useCallback(
    (options: { title?: string; message: string; confirmLabel?: string; cancelLabel?: string }) => {
      return new Promise<boolean>((resolve) => {
        confirmDialogResolverRef.current = resolve;
        setConfirmDialogState({
          open: true,
          title: options.title || 'Onay gerekli',
          message: options.message,
          confirmLabel: options.confirmLabel || 'Onayla',
          cancelLabel: options.cancelLabel || 'İptal',
        });
      });
    },
    [],
  );

  const handleConfirmDialogClose = (result: boolean) => {
    setConfirmDialogState((prev) => ({ ...prev, open: false }));
    confirmDialogResolverRef.current?.(result);
    confirmDialogResolverRef.current = null;
  };

  // Helper function to extract ID from GSC API message
  const extractIdFromMessage = (message: string): string | null => {
    // Pattern: "Activity Track with ID: 1017 successfully created"
    // Or: "Other Fire-Fighting Works Task with ID: '1018' successfully updated"
    // Or: "Activity track created with status 'pending'. ID: 7260fb16-2fb1-437e-90af-3f84d67c3dd4"

    // First try to match UUID format (for localActivityId in message)
    const uuidMatch = message.match(/ID:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (uuidMatch) {
      return uuidMatch[1];
    }

    // Then try to match numeric ID (for GSC API IDs)
    const numericMatch = message.match(/ID:\s*'?(\d+)'?/i);
    return numericMatch ? numericMatch[1] : null;
  };

  // Helper function to validate UUID format
  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  // Helper function to check if current time is in night mode
  const isNightModeActive = useCallback((): boolean => {
    if (!hotelSettings?.settings?.nightModeEnabled) {
      return false;
    }

    const startTime = hotelSettings.settings.nightModeStartTime || '18:00';
    const endTime = hotelSettings.settings.nightModeEndTime || '08:00';

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;

    // Parse start and end times
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startTimeMinutes = startHour * 60 + startMin;
    const endTimeMinutes = endHour * 60 + endMin;

    // Handle cases where night mode spans across midnight
    if (startTimeMinutes > endTimeMinutes) {
      // Night mode crosses midnight (e.g., 18:00 to 08:00)
      return currentTimeMinutes >= startTimeMinutes || currentTimeMinutes < endTimeMinutes;
    } else {
      // Night mode doesn't cross midnight (e.g., 22:00 to 23:59)
      return currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < endTimeMinutes;
    }
  }, [hotelSettings]);

  // Compute effective AI enabled state (considers both chat setting and night mode)
  const getEffectiveAIEnabled = useCallback((): boolean => {
    if (!chat) return false;
    // AI is effectively enabled if either explicitly enabled OR night mode is active
    return chat.aiEnabled || isNightModeActive();
  }, [chat, isNightModeActive]);

  // Update AI state when chat is loaded
  useEffect(() => {
    if (chat) {
      setIsAIEnabled(getEffectiveAIEnabled());
    }
  }, [chat, getEffectiveAIEnabled]);

  // Fetch hotel settings for night mode
  const fetchHotelSettings = useCallback(async () => {
    if (!user?.hotelId) return;

    try {
      const response = await api.get(`/hotels/${user.hotelId}`);
      setHotelSettings(response.data);
      console.log('🌙 [NightMode] Hotel settings loaded:', {
        nightModeEnabled: response.data?.settings?.nightModeEnabled,
        startTime: response.data?.settings?.nightModeStartTime,
        endTime: response.data?.settings?.nightModeEndTime,
      });
    } catch (error) {
      console.error('Error fetching hotel settings:', error);
    }
  }, [user?.hotelId]);

  // Fetch configuration data from GSC API and hotel settings
  useEffect(() => {
    fetchConfigurationData();
    fetchHotelSettings();
  }, [user?.hotelId, fetchHotelSettings]);

  const fetchConfigurationData = async () => {
    try {
      setConfigLoading(true);

      const unwrapPayload = (payload: any) => {
        if (payload == null) return null;
        if (Array.isArray(payload)) return payload;
        if (typeof payload !== 'object') return payload;

        if (Array.isArray(payload.data) || (payload.data && typeof payload.data === 'object')) {
          return payload.data;
        }
        if (Array.isArray(payload.Data) || (payload.Data && typeof payload.Data === 'object')) {
          return payload.Data;
        }

        return payload;
      };

      // Fetch all configuration data in parallel
      const [activityTypesRes, serviceTaskConfigRes, guestCommentConfigRes] = await Promise.allSettled([
        api.get('/gsc-api/activity-types'),
        api.get('/gsc-api/service-tasks/config'),
        api.get('/gsc-api/guest-comments/config'),
      ]);

      if (activityTypesRes.status === 'fulfilled') {
        // Tüm aktivite tipleri – cevap doğrudan array veya { data: array } olabilir
        const payload = unwrapPayload(activityTypesRes.value.data);
        const raw = Array.isArray(payload)
          ? payload
          : Array.isArray((payload as any)?.data)
            ? (payload as any).data
            : Array.isArray((payload as any)?.Data)
              ? (payload as any).Data
              : [];
        setActivityTypes(raw);
        console.log('Activity Types loaded:', raw?.length, raw);
      }

      if (serviceTaskConfigRes.status === 'fulfilled') {
        const serviceTaskPayload = unwrapPayload(serviceTaskConfigRes.value.data) as ServiceTaskConfig | null;
        setServiceTaskConfig(serviceTaskPayload);
        console.log('Service Task Config loaded:', serviceTaskPayload);
        // Debug: Check one group structure to verify Department linkage
        if (serviceTaskPayload?.Groups?.length) {
          console.log('First Service Task Group Sample:', serviceTaskPayload.Groups[0]);
        }
      }

      if (guestCommentConfigRes.status === 'fulfilled') {
        const guestCommentPayload = unwrapPayload(guestCommentConfigRes.value.data) as GuestCommentConfig | null;
        setGuestCommentConfig(guestCommentPayload);
        console.log('Guest Comment Config loaded:', guestCommentPayload);
      }
    } catch (err: any) {
      console.error('Failed to load configuration data:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  // --- Data Fetching and Real-time Hooks ---
  useEffect(() => {
    if (id) {
      fetchChat();
      fetchMessages();
      joinChat(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Pre-fill and lock locality if guest has a room (and it's not the virtual room '000')
  // Pre-fill and lock locality if guest has a room (and it's not the virtual room '000')
  useEffect(() => {
    if (serviceTaskModalOpen && serviceTaskConfig?.Localities && chat?.room?.roomNumber && chat.room.roomNumber !== '000') {
      const roomNumber = chat.room.roomNumber;
      console.log('🔍 [AutoSelectLocality] Looking for room:', roomNumber);

      // Filter localities of type 'Room'
      const roomLocalities = serviceTaskConfig.Localities.filter(l => l.Type === 'Room');
      console.log(`🔍 [AutoSelectLocality] Found ${roomLocalities.length} room localities available`);

      // Find locality ID matching the room number
      // enhanced matching logic
      const locality = roomLocalities.find(l => {
        const desc = l.Description.trim();
        return desc === roomNumber ||
          desc === `Room ${roomNumber}` ||
          desc.endsWith(` ${roomNumber}`) ||
          desc.includes(roomNumber);
      });

      if (locality && locality.ID) { // Ensure ID exists
        console.log('✅ [AutoSelectLocality] Found matching locality:', locality);
        setServiceLocalityType('Room');
        // Small timeout to ensure the UI updates if Select component needs type to be set first
        // And double check we are not setting a UUID if for some reason Config has one (unlikely but safe)
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(locality.ID));
        if (!isUUID) {
          setTimeout(() => setServiceLocality(String(locality.ID)), 50);
        } else {
          console.warn('⚠️ [AutoSelectLocality] Found locality has UUID as ID, skipping auto-select to avoid MUI error:', locality);
        }
      } else {
        console.warn('⚠️ [AutoSelectLocality] Room number exists but no matching locality found in config.');
        setServiceLocalityType('Room');
      }
    }
  }, [serviceTaskModalOpen, chat, serviceTaskConfig]);

  useEffect(() => {
    if (connection) {
      const handleNewMessage = (message: Message) => {
        if (message.chatId === id) {
          setMessages((prev) => {
            // Check for existence or optimistic replacement
            const isOptimistic = prev.some(m => m.id.startsWith('temp-') && m.content === message.content && m.senderId === message.senderId);
            const exists = prev.some(m => m.id === message.id);

            let updatedPrev = prev;
            if (isOptimistic) {
              updatedPrev = prev.filter(m => !m.id.startsWith('temp-') || m.senderId !== message.senderId || m.content !== message.content);
            } else if (exists) {
              return prev;
            }

            const newMessages = [...updatedPrev, message];

            if (message.isFromLLM) {
              setIsLLMTyping(false);
            }

            return newMessages;
          });
        }
      };

      connection.on('NewMessage', handleNewMessage);

      const handleMessageChunk = (chunk: { messageId: string, chunk: string }) => {
        if (!chunk.messageId || !chunk.chunk) return;
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id === chunk.messageId) {
              const newContent = m.content === '...' ? chunk.chunk : m.content + chunk.chunk;
              return { ...m, content: newContent };
            }
            return m;
          }),
        );
      };

      const handleMessageUpdated = (message: Message) => {
        setMessages((prev) => prev.map((m) => (m.id === message.id ? message : m)));
      };

      connection.on('MessageChunk', handleMessageChunk);
      connection.on('MessageUpdated', handleMessageUpdated);

      // Handle AI status changes
      const handleAIStatusChanged = (data: { chatId: string, aiEnabled: boolean }) => {
        if (data.chatId === id) {
          console.log('[AIStatusChanged]', data);
          if (chat) {
            const updatedChat = { ...chat, aiEnabled: data.aiEnabled };
            setChat(updatedChat);
            // Update effective AI status considering night mode
            setIsAIEnabled(data.aiEnabled || isNightModeActive());
          }
        }
      };

      connection.on('AIStatusChanged', handleAIStatusChanged);

      return () => {
        connection.off('NewMessage', handleNewMessage);
        connection.off('MessageChunk', handleMessageChunk);
        connection.off('MessageUpdated', handleMessageUpdated);
        connection.off('AIStatusChanged', handleAIStatusChanged);
      };
    }
  }, [connection, id, chat, isNightModeActive]);

  const scrollToBottom = useCallback(() => {
    const element = messagesEndRef.current?.parentElement;
    if (element) {
      // Scroll only if user is near the bottom (within 200px)
      const isNearBottom = element.scrollHeight - element.scrollTop < element.clientHeight + 200;
      if (isNearBottom || messages.length <= 1) { // Always scroll for the first message
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLLMTyping, scrollToBottom]);

  const fetchChat = async () => {
    try {
      const response = await api.get(`/chats/${id}`);
      console.log('📝 [fetchChat] Chat data received:', {
        chatId: response.data.id,
        chatName: response.data.name,
        hasReservation: !!response.data.reservation,
        reservationId: response.data.reservationId,
        reservation: response.data.reservation,
      });
      setChat(response.data);
      setIsAIEnabled(response.data.isLLMPresent || false); // Initialize AI state from chat data
    } catch (error) {
      console.error('Error fetching chat:', error);
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/chats/${id}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Fetch AI text suggestion with debouncing
  const fetchTextSuggestion = useCallback(async (text: string) => {
    console.log('🤖 [Text Suggestion] Starting fetch for text:', text);

    if (!text.trim() || text.trim().length < 3) {
      console.log('🤖 [Text Suggestion] Text too short, skipping');
      setTextSuggestion(null);
      return;
    }

    // Check if this text was already sent
    if (lastSentMessageRef.current.trim() === text.trim()) {
      console.log('🤖 [Text Suggestion] Text already sent, skipping suggestion');
      setTextSuggestion(null);
      return;
    }

    // Only show suggestions for operators and admins
    if (user?.role !== 'operator' && user?.role !== 'admin') {
      console.log('🤖 [Text Suggestion] User role not authorized:', user?.role);
      return;
    }

    try {
      console.log('🤖 [Text Suggestion] Calling API for chat:', id);
      setIsFetchingSuggestion(true);
      const response = await api.post(`/chats/${id}/suggest-text`, { text });
      console.log('🤖 [Text Suggestion] API Response:', response.data);

      // Double-check: if message was sent while API was in flight, don't show suggestion
      if (lastSentMessageRef.current.trim() === text.trim()) {
        console.log('🤖 [Text Suggestion] Message was sent while API was in flight, ignoring suggestion');
        setTextSuggestion(null);
        return;
      }

      setTextSuggestion(response.data.suggestion);
      console.log('🤖 [Text Suggestion] Suggestion set:', response.data.suggestion);
    } catch (error: any) {
      console.error('❌ [Text Suggestion] Error fetching:', error);
      console.error('❌ [Text Suggestion] Error response:', error?.response?.data);
      // Silently fail, don't disrupt user experience
      setTextSuggestion(null);
    } finally {
      setIsFetchingSuggestion(false);
      console.log('🤖 [Text Suggestion] Fetch completed');
    }
  }, [id, user?.role]);

  // Debounced text change handler
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = e.target.value;
    console.log('✏️ [Text Input] Text changed:', text);
    setNewMessage(text);

    // Clear existing timeout
    if (suggestionTimeoutRef.current) {
      console.log('⏱️ [Text Input] Clearing previous timeout');
      clearTimeout(suggestionTimeoutRef.current);
    }

    // Clear suggestion if text is empty
    if (!text.trim()) {
      console.log('✏️ [Text Input] Empty text, clearing suggestion');
      setTextSuggestion(null);
      lastSentMessageRef.current = ''; // Reset when text is cleared
      return;
    }

    // Reset lastSentMessageRef if user is typing new content (different from last sent message)
    if (text.trim() !== lastSentMessageRef.current.trim()) {
      lastSentMessageRef.current = '';
    }

    // Set new timeout for 2 seconds
    console.log('⏱️ [Text Input] Setting 2-second timeout for suggestion');
    suggestionTimeoutRef.current = setTimeout(() => {
      console.log('⏱️ [Text Input] Timeout fired, fetching suggestion');
      fetchTextSuggestion(text);
    }, 2000);
  };

  // Accept suggestion
  const acceptSuggestion = () => {
    console.log('✅ [Suggestion] Accepting suggestion:', textSuggestion);
    if (textSuggestion) {
      setNewMessage(textSuggestion);
      setTextSuggestion(null);
    }
  };

  // Dismiss suggestion
  const dismissSuggestion = () => {
    console.log('❌ [Suggestion] Dismissing suggestion');
    setTextSuggestion(null);
  };

  const getDefaultActivityGuestName = useCallback((message?: Message | null): string => {
    if (message?.sender?.role === 'guest') {
      const fullName = `${message.sender.firstName || ''} ${message.sender.lastName || ''}`.trim();
      if (fullName) return fullName;
    }

    const guestSender = messages.find((m) => m.sender?.role === 'guest')?.sender;
    if (guestSender) {
      const fullName = `${guestSender.firstName || ''} ${guestSender.lastName || ''}`.trim();
      if (fullName) return fullName;
    }

    const chatName = chat?.name?.split('-')?.[0]?.trim();
    return chatName || '';
  }, [chat?.name, messages]);

  useEffect(() => {
    const fetchDetailedGuestProfile = async () => {
      const messageGuest = messages.find((m) => m.sender?.role === 'guest')?.sender as any;
      const roomGuest = ((chat as any)?.room?.guests || []).find((u: any) => u?.role === 'guest');

      const profileCandidates = [
        chat?.reservation?.profileId,
        roomGuest?.profileId,
        messageGuest?.profileId,
      ]
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter(Boolean)
        .filter((v, idx, arr) => arr.indexOf(v) === idx)
        .filter((v) => !isUUIDLike(v));

      const profileID = profileCandidates[0] || null;

      // PMS reservation ID should be used here; local UUID reservation IDs create "Profile not found" noise.
      const reservationIDRaw = chat?.reservation?.reservationId || chat?.reservationId;
      const reservationID = reservationIDRaw && !isUUIDLike(String(reservationIDRaw))
        ? String(reservationIDRaw)
        : undefined;

      if (!profileID || !chat?.id) {
        setDetailedGuestProfile(null);
        return;
      }

      try {
        const response = await api.get('/gsc-api/guest-profile', {
          params: {
            profileID,
            reservationID,
            isDetailed: true,
          },
        });
        const payload = response.data?.data || response.data?.Data || response.data;
        setDetailedGuestProfile(payload || null);
      } catch (err: any) {
        // This call is best-effort for tooltip enrichment; ignore known "Profile not found" responses.
        const status = err?.response?.status;
        const message = err?.response?.data?.Message || err?.response?.data?.message || '';
        if (!(status === 400 && String(message).toLowerCase().includes('profile not found'))) {
          console.warn('[GuestDetail] Failed to fetch detailed guest profile', err);
        }
        setDetailedGuestProfile(null);
      }
    };

    fetchDetailedGuestProfile();
  }, [chat, messages]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, []);

  // --- Image Handling Functions ---
  const handleImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);

      // Create a preview URL for the selected image
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // --- Action Handlers ---
  const handleMessageAction = (action: string, message: Message) => {
    setSelectedMessage(message);

    switch (action) {
      case 'reply':
        setReplyToMessage(message);
        break;
      case 'comment':
        resetGuestCommentForm();
        setCommentText(message.content);
        setCommentModalOpen(true);
        break;
      case 'activity':
        setActivityModalOpen(true);
        setActivityName(getDefaultActivityGuestName(message));
        // Set default date to today
        setActivityDate(new Date().toISOString().split('T')[0]);
        break;
      case 'service':
        setServiceTaskModalOpen(true);
        setServiceNotes(message.content);
        // Pre-fill room ID (UUID) if available - we'll display the room number in the form
        const roomId = chat?.roomId || message.sender?.roomId || '';
        setServiceLocality(roomId);
        // Reset filter states
        setServiceTaskDepartment('');
        setServiceTaskGroup('');
        setServiceTaskType('');
        break;
    }
  };

  const cancelReply = () => {
    setReplyToMessage(null);
  };

  // --- Message Sending Logic ---
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedImage) || !id || !user) return;

    const messageContent = newMessage;
    const replyToId = replyToMessage?.id;

    // Clear suggestion timeout and suggestion immediately when sending
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
      suggestionTimeoutRef.current = null;
    }
    setTextSuggestion(null);

    // Track the sent message to prevent suggestions for it
    if (messageContent.trim()) {
      lastSentMessageRef.current = messageContent.trim();
    }

    setNewMessage('');
    setReplyToMessage(null); // Clear reply after sending
    setError(null);

    const optimisticMessages: Message[] = [];

    try {
      // If there's an image, upload it first and send as separate message
      if (selectedImage) {
        setIsUploadingImage(true);
        const formData = new FormData();
        formData.append('image', selectedImage);

        try {
          const response = await api.post('/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          // Create optimistic message for image
          const optimisticImageMessage: Message = {
            id: `temp-image-${Date.now()}`,
            chatId: id,
            content: response.data.url,
            type: MessageType.IMAGE,
            senderId: user.id,
            sender: user,
            isFromLLM: false,
            createdAt: new Date().toISOString(),
            replyToId,
            replyTo: replyToMessage || undefined,
          };

          optimisticMessages.push(optimisticImageMessage);
          setMessages((prev) => [...prev, optimisticImageMessage]);

          // Send the image as a separate message
          const imageUrl = response.data.url;

          if (isConnected) {
            sendMessage(id, imageUrl, MessageType.IMAGE, replyToId);
          } else {
            await api.post(`/chats/${id}/messages`, {
              content: imageUrl,
              type: MessageType.IMAGE,
              replyToId,
            });
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          setError(t('chat.failedToUploadImage'));
          setIsUploadingImage(false);
          // Remove optimistic image message on error
          setMessages((prev) => prev.filter(m => !optimisticMessages.some(om => om.id === m.id)));
          return;
        } finally {
          setIsUploadingImage(false);
        }
      }

      // If there's also text, send it as a separate text message
      if (messageContent.trim()) {
        // Create optimistic message for text
        const optimisticTextMessage: Message = {
          id: `temp-text-${Date.now()}`,
          chatId: id,
          content: messageContent,
          type: MessageType.TEXT,
          senderId: user.id,
          sender: user,
          isFromLLM: false,
          createdAt: new Date().toISOString(),
          replyToId: selectedImage ? undefined : replyToId,
          replyTo: selectedImage ? undefined : (replyToMessage || undefined),
        };

        optimisticMessages.push(optimisticTextMessage);
        setMessages((prev) => [...prev, optimisticTextMessage]);

        // Only show typing indicator if AI is enabled AND we're sending a text message
        if (chat?.isLLMPresent && isAIEnabled && messageContent.trim()) {
          setIsLLMTyping(true);
          setTimeout(() => {
            setIsLLMTyping(false);
          }, 45000);
        }

        if (isConnected) {
          // For socket connection, send with replyToId (only on first message if there's both)
          sendMessage(id, messageContent, MessageType.TEXT, selectedImage ? undefined : replyToId);
        } else {
          // For HTTP request, send the message data
          await api.post(`/chats/${id}/messages`, {
            content: messageContent,
            type: MessageType.TEXT,
            replyToId: selectedImage ? undefined : replyToId,
          });
        }
      }

      // Clear the selected image after sending
      clearSelectedImage();
    } catch (error) {
      console.error('Error sending message:', error);
      setError(t('chat.failedToSendMessage'));
      setIsLLMTyping(false);
      // Remove all optimistic messages on error
      setMessages((prev) => prev.filter(m => !optimisticMessages.some(om => om.id === m.id)));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // --- Modal Handlers ---
  const resetGuestCommentForm = () => {
    setCommentDepartment('');
    setCommentGroup('');
    setCommentType('');
    setCommentText('');
    setCommentStatus('Positive');
  };

  const handleGuestCommentSubmit = async () => {
    if (!chat || !selectedMessage) return;

    // Get guest user from the selected message
    const guestUser = selectedMessage.sender;
    if (!guestUser || guestUser.role !== 'guest') {
      setError('Cannot create guest comment: Guest user not found');
      return;
    }

    // At this point, guestUser is guaranteed to be defined
    const guestName = `${guestUser.firstName} ${guestUser.lastName}`;

    const profileId =
      guestUser.profileId
      || chat.reservation?.profileId
      || undefined;
    const reservationIdRaw =
      guestUser.reservation?.reservationId
      || chat.reservation?.reservationId
      || guestUser.reservationId
      || chat.reservationId
      || '';
    const reservationId = String(reservationIdRaw || '').trim();
    const safeReservationId = reservationId && !isUUIDLike(reservationId) ? reservationId : undefined;

    if (!profileId || !safeReservationId) {
      setError('Cannot create guest comment: PMS ProfileID/ReservationID bulunamadı');
      return;
    }

    try {
      const createRes = await api.post('/gsc-api/guest-comments', {
        CommentTypeID: commentType,
        ProfileID: profileId,
        ReservationID: safeReservationId,
        Status: commentStatus,
        CommentText: commentText,
      });

      const commentId = parsePmsGuestCommentId(createRes.data);
      const parsedType = commentType ? parseInt(commentType, 10) : NaN;

      // Send system message with guest comment metadata
      const guestCommentMetadata: GuestCommentMetadata = {
        guestName,
        commentText,
        status: commentStatus as 'Positive' | 'Neutral' | 'Negative',
        commentTypeId: Number.isFinite(parsedType) ? parsedType : undefined,
        commentTypeID: commentType || undefined,
        commentId,
        pendingCommentId: commentId == null,
        profileId,
        reservationId: safeReservationId,
      };

      await api.post(`/chats/${id}/messages`, {
        content: t('chat.guestCommentSaved'),
        type: MessageType.SYSTEM,
        metadata: guestCommentMetadata,
      });

      setCommentModalOpen(false);
      resetGuestCommentForm();
      setError(null);
    } catch (error: any) {
      console.error('Error creating guest comment:', error);
      setError(error?.response?.data?.message || 'Failed to create guest comment');
    }
  };

  const handleGuestCommentReply = async (message: Message, replyNote: string) => {
    if (!id) return;
    const meta = message.metadata as GuestCommentMetadata;
    let commentId = meta.commentId;
    const commentTypeID =
      meta.commentTypeID ??
      (meta.commentTypeId != null ? String(meta.commentTypeId) : '');
    const trimmed = replyNote.trim();
    if (!trimmed) {
      setError(t('chat.guestCommentReplyEmpty'));
      throw new Error('empty');
    }
    const resolvedProfileId =
      meta.profileId
      || message.sender?.profileId
      || chat?.reservation?.profileId
      || undefined;
    const resolvedReservationIdRaw =
      meta.reservationId
      || message.sender?.reservation?.reservationId
      || chat?.reservation?.reservationId
      || message.sender?.reservationId
      || chat?.reservationId
      || '';
    const resolvedReservationId = String(resolvedReservationIdRaw || '').trim();
    const safeResolvedReservationId =
      resolvedReservationId && !isUUIDLike(resolvedReservationId) ? resolvedReservationId : undefined;

    if (
      !resolvedProfileId ||
      !safeResolvedReservationId ||
      !commentTypeID
    ) {
      setError(t('chat.guestCommentReplyMissingIds'));
      throw new Error('missing');
    }

    if (commentId == null) {
      try {
        const profileRes = await api.get('/gsc-api/guest-profile', {
          params: {
            profileID: resolvedProfileId,
            reservationID: safeResolvedReservationId,
            isDetailed: true,
          },
        });
        const profilePayload = profileRes.data?.data || profileRes.data?.Data || profileRes.data;
        commentId = resolveCommentIdFromGuestProfile(profilePayload, {
          commentText: meta.commentText,
          commentTypeID,
          status: meta.status,
        });
      } catch (resolveError) {
        console.warn('[GuestCommentReply] Failed to resolve pending CommentID', resolveError);
      }
    }

    if (commentId == null) {
      setError('Yorum ID henuez olusmamis veya bulunamadi. Lütfen birkaç saniye sonra tekrar deneyin.');
      throw new Error('pending-comment-id');
    }

    try {
      await api.put(`/gsc-api/guest-comments/${commentId}`, {
        CommentID: commentId,
        CommentTypeID: commentTypeID,
        ProfileID: resolvedProfileId,
        ReservationID: safeResolvedReservationId,
        Status: meta.status,
        CommentText: meta.commentText,
        ReplyNote: trimmed,
      });
      const nextMeta: GuestCommentMetadata = {
        ...meta,
        commentId,
        commentTypeID,
        pendingCommentId: false,
        profileId: resolvedProfileId,
        reservationId: safeResolvedReservationId,
        replyNote: trimmed,
      };
      const { data: updatedMessage } = await api.put<Message>(`/chats/${id}/messages/${message.id}`, {
        metadata: nextMeta,
      });
      setMessages((prev) => prev.map((m) => (m.id === message.id ? updatedMessage : m)));
      setError(null);
    } catch (error: any) {
      console.error('Error saving guest comment reply:', error);
      setError(error?.response?.data?.message || t('chat.guestCommentReplyFailed'));
      throw error;
    }
  };

  const handleActivityReservationSubmit = async () => {
    if (!chat) return;

    // Get guest user - either from the selected message or from metadata if editing
    let guestUser = selectedMessage?.sender;
    let guestName = '';
    let isEditing = false;

    // Check if we're editing an existing activity (selectedMessage is the activity card)
    if (selectedMessage?.type === MessageType.ACTIVITY_RESERVATION && selectedMessage.metadata) {
      isEditing = true;
      const metadata = selectedMessage.metadata as ActivityReservationMetadata;
      guestName = metadata.guestName;
      // For editing, we don't need the full guest user object, just use the data from form
    } else if (selectedMessage?.sender?.role === 'guest') {
      // Creating new activity from guest message
      guestUser = selectedMessage.sender;
      if (!guestUser) {
        setError('Cannot create activity reservation: Guest user not found');
        return;
      }
      guestName = `${guestUser.firstName} ${guestUser.lastName}`;
    } else {
      setError('Cannot create activity reservation: Guest information not found');
      return;
    }

    // Note: We no longer block if GSC data is missing - backend will lazy load it
    // Just ensure we have a reservation ID for the backend to work with
    const reservationId = chat?.reservation?.id || chat?.reservationId;

    console.log('🔍 [Activity Creation] Checking reservation:', {
      hasChat: !!chat,
      chatId: chat?.id,
      chatName: chat?.name,
      hasReservationField: 'reservation' in (chat || {}),
      reservationValue: chat?.reservation,
      reservationId: chat?.reservation?.id,
      directReservationId: chat?.reservationId,
      finalReservationId: reservationId,
    });

    if (!reservationId) {
      setError('Cannot create activity: No reservation found for this chat');
      return;
    }

    // Validate date is within reservation period
    if (chat?.reservation && activityDate) {
      const selectedDate = new Date(activityDate);
      const checkIn = new Date(chat.reservation.checkInDate);
      const checkOut = new Date(chat.reservation.checkOutDate);

      // Set time to midnight for accurate date comparison
      selectedDate.setHours(0, 0, 0, 0);
      checkIn.setHours(0, 0, 0, 0);
      checkOut.setHours(0, 0, 0, 0);

      if (selectedDate < checkIn || selectedDate > checkOut) {
        setError(`Activity date must be within the reservation period (${chat.reservation.checkInDate.split('T')[0]} to ${chat.reservation.checkOutDate.split('T')[0]})`);
        return;
      }
    }

    const isShuttleType = selectedActivityType?.InternalType === 'ShuttleReservation';
    const sessionIds: string[] = isShuttleType
      ? [shuttleOutboundSession, shuttleReturnSession].filter(Boolean)
      : (activitySession ? [activitySession] : []);

    try {
      let activityId;
      let previousStatus: ActivityReservationMetadata['status'] | undefined;

      if (isEditing && selectedMessage?.metadata) {
        // Update existing activity
        const existingMetadata = selectedMessage.metadata as ActivityReservationMetadata;
        previousStatus = existingMetadata.status;
        activityId = existingMetadata.activityId;

        if (activityId !== undefined && activityId !== null) {
          await api.put(`/gsc-api/activity-tracks/${activityId}`, {
            ProfileID: chat.reservation?.profileId || undefined,
            ReservationID: chat.reservation?.reservationId || undefined,
            localReservationId: reservationId, // For lazy loading GSC data
            Date: activityDate, // Already in YYYY-MM-DD format from date input
            TypeID: activityTypeId,
            SessionIDs: sessionIds.length > 0 ? sessionIds : undefined,
            Notes: `${activityName} - ${activityEventName}`,
          });
        }
      } else {
        // Create new activity
        const chatIdParam = id ? `?chatId=${id}` : '';
        const response = await api.post(`/gsc-api/activity-tracks${chatIdParam}`, {
          ProfileID: chat.reservation?.profileId || undefined,
          ReservationID: chat.reservation?.reservationId || undefined,
          localReservationId: reservationId, // For lazy loading GSC data
          Date: activityDate, // Already in YYYY-MM-DD format from date input
          TypeID: activityTypeId,
          SessionIDs: sessionIds.length > 0 ? sessionIds : undefined,
          Notes: `${activityName} - ${activityEventName}`,
        });

        console.log('📝 [handleActivityReservationSubmit] Activity API response:', {
          ResultID: response.data?.ResultID,
          ID: response.data?.ID,
          Message: response.data?.Message,
          State: response.data?.State,
          FullResponse: response.data
        });

        // Check if the request was rejected by PMS
        if (response.data?.State === 'Rejected') {
          const errorMessage = response.data?.Message || 'Aktivite oluşturulamadı. PMS sistemi tarafından reddedildi.';
          console.error('❌ [handleActivityReservationSubmit] Activity creation rejected by PMS:', errorMessage);
          setError(errorMessage);
          showInfoDialog(`Aktivite oluşturulamadı: ${errorMessage}`, { severity: 'error', title: 'Aktivite Hatası' });
          return;
        }

        // Priority: Use localActivityId (UUID) from backend, then GSC API IDs, then parse from message
        // Backend returns localActivityId which is the database UUID for the activity track
        activityId = response.data?.localActivityId || response.data?.ResultID || response.data?.ID;

        // If still not found, try to parse from Message field (for backward compatibility)
        // Note: extractIdFromMessage may extract numeric IDs from UUIDs, so use this as last resort
        if (!activityId && response.data?.Message) {
          const parsedId = extractIdFromMessage(response.data.Message);
          if (parsedId) {
            // Check if parsedId looks like a UUID (contains hyphens)
            // If it's a UUID, we should use localActivityId instead
            if (response.data?.localActivityId) {
              activityId = response.data.localActivityId;
              console.log('✅ Using localActivityId (UUID) instead of parsed numeric ID:', activityId);
            } else {
              activityId = parsedId;
              console.log('✅ Extracted activity ID from message:', parsedId);
            }
          }
        }

        // Warn if activityId is still null/undefined after parsing
        if (!activityId) {
          console.warn('⚠️ [handleActivityReservationSubmit] Activity ID not found in response', {
            responseData: response.data,
            allKeys: response.data ? Object.keys(response.data) : [],
            State: response.data?.State,
          });

          const shouldContinue = await requestConfirmation({
            title: 'Eksik Aktivite ID',
            message:
              'Aktivite oluşturuldu ancak PMS sisteminden aktivite ID alınamadı.\n\n' +
              'Bu aktiviteyi iptal edemeyebilirsiniz.\n\n' +
              'Yine de devam etmek istiyor musunuz?',
            confirmLabel: 'Devam Et',
            cancelLabel: 'İptal',
          });

          if (!shouldContinue) {
            console.log('🚫 [handleActivityReservationSubmit] User cancelled due to missing activity ID');
            return;
          }
        }
      }

      // Build a human-readable summary of the chosen session(s)
      const sessionSummary = (() => {
        if (isShuttleType) {
          const parts: string[] = [];
          if (shuttleOutboundSession) {
            const s = selectedActivityType?.Sessions?.find(s => s.ID === shuttleOutboundSession);
            if (s) parts.push(`→ ${s.Code} ${s.Time ? new Date(s.Time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}`.trim());
          }
          if (shuttleReturnSession) {
            const s = selectedActivityType?.Sessions?.find(s => s.ID === shuttleReturnSession);
            if (s) parts.push(`← ${s.Code} ${s.Time ? new Date(s.Time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}`.trim());
          }
          return parts.length > 0 ? parts.join(' / ') : 'Not specified';
        }
        return activitySession || 'Not specified';
      })();

      // Update or create activity reservation card in chat
      const activityMetadata: ActivityReservationMetadata = {
        activityId: activityId || undefined, // Store undefined instead of null for better JSON handling
        guestName: guestName,
        activityName: activityEventName,
        date: activityDate,
        session: sessionSummary,
        notes: activityName,
        // Keep existing status when editing, otherwise start as pending
        status: (isEditing && previousStatus) ? previousStatus : 'pending',
      };

      console.log('✅ Saving activity metadata:', {
        activityId,
        hasActivityId: !!activityId,
        metadata: activityMetadata,
        isEditing
      });

      if (isEditing && selectedMessage) {
        // Update existing message metadata locally in our API;
        // for new activities, the backend (GSC API service) already creates the chat message/card.
        await api.put(`/chats/${id}/messages/${selectedMessage.id}`, {
          metadata: activityMetadata,
        });
        console.log('✅ Activity message updated');
      }

      setActivityModalOpen(false);
      setActivityName('');
      setActivityEventName('');
      setActivityDate('');
      setActivitySession('');
      setShuttleOutboundSession('');
      setShuttleReturnSession('');
      setSelectedMessage(null);
      setError(null);

      const successMessage = activityId
        ? `${activityEventName} aktivitesi başarıyla oluşturuldu.`
        : `${activityEventName} aktivitesi oluşturuldu ancak PMS ID alınamadı. Bu aktiviteyi iptal edemeyebilirsiniz.`;

      showInfoDialog(isEditing ? 'Aktivite rezervasyonu başarıyla güncellendi!' : successMessage, {
        severity: 'success',
        title: 'İşlem Başarılı',
      });
    } catch (error: any) {
      console.error('Error with activity reservation:', error);
      setError(error?.response?.data?.message || 'Failed to save activity reservation');
    }
  };

  // Edit activity reservation
  const handleEditActivity = (message: Message) => {
    const metadata = message.metadata as ActivityReservationMetadata;
    if (!metadata) return;

    // Pre-fill the form with existing data
    setActivityEventName(metadata.activityName);
    setActivityDate(metadata.date);
    setActivitySession(metadata.session);
    setShuttleOutboundSession('');
    setShuttleReturnSession('');
    setActivityName(metadata.notes || '');

    // Set the message as selected for updating
    setSelectedMessage(message);
    setActivityModalOpen(true);
  };

  // Confirm activity reservation (send to GSC API)
  const handleConfirmActivity = async (message: Message) => {
    const metadata = message.metadata as ActivityReservationMetadata;
    console.log('🔍 [handleConfirmActivity] Checking activity metadata:', {
      metadata,
      activityId: metadata?.activityId,
      activityName: metadata?.activityName,
      hasMetadata: !!metadata,
      metadataKeys: metadata ? Object.keys(metadata) : [],
      fullMessage: message
    });

    if (!metadata || !metadata.activityId) {
      console.error('❌ [handleConfirmActivity] Activity metadata missing or invalid:', JSON.stringify(metadata, null, 2));
      showInfoDialog('Aktivite ID bulunamadı. Lütfen konsolu kontrol edin.', {
        severity: 'error',
        title: 'Eksik Bilgi',
      });
      return;
    }

    const activityName = metadata.activityName || t('chat.activity');
    const confirmActivity = await requestConfirmation({
      title: t('chat.confirmActivityTitle'),
      message: `${activityName} ${t('chat.confirmActivity')}`,
      confirmLabel: t('chat.confirm'),
      cancelLabel: t('login.cancel'),
    });
    if (!confirmActivity) {
      console.log('🚫 [handleConfirmActivity] User cancelled the confirmation');
      return;
    }

    console.log(`📤 [handleConfirmActivity] Confirming activity track ID: ${metadata.activityId}`);

    try {
      // Call backend to confirm the activity (send to GSC API)
      console.log(`📡 [handleConfirmActivity] Calling POST /gsc-api/activity-tracks/${metadata.activityId}/confirm`);
      const response = await api.post(`/gsc-api/activity-tracks/${metadata.activityId}/confirm`);
      console.log('✅ [handleConfirmActivity] Confirm API response:', response.data);

      // Update the message to mark as confirmed
      const updatedMetadata: ActivityReservationMetadata = {
        ...metadata,
        status: 'confirmed',
        activityId: response.data?.gscTrackId || metadata.activityId, // Update with GSC track ID if available
      };

      // Update message in our database
      console.log(`💾 [handleConfirmActivity] Updating message ${message.id} in database`);
      await api.put(`/chats/${id}/messages/${message.id}`, {
        metadata: updatedMetadata,
      });

      // Update message locally
      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id
            ? { ...m, metadata: updatedMetadata }
            : m
        )
      );

      console.log('✅ [handleConfirmActivity] Activity confirmed successfully');
      showInfoDialog(`${activityName} aktivitesi başarıyla onaylandı ve PMS sistemine gönderildi.`, {
        severity: 'success',
        title: 'İşlem Başarılı',
      });
    } catch (error: any) {
      console.error('❌ [handleConfirmActivity] Error confirming activity:', error);
      console.error('❌ [handleConfirmActivity] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });

      const errorMessage = error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Aktivite onaylanırken bir hata oluştu.';

      setError(errorMessage);
      showInfoDialog(`Aktivite onaylanamadı: ${errorMessage}`, {
        severity: 'error',
        title: 'Aktivite Hatası',
      });
    }
  };

  const handleConfirmServiceTask = async (message: Message) => {
    const metadata = message.metadata as ServiceTaskMetadata;
    console.log('🔍 [handleConfirmServiceTask] Checking service task metadata:', {
      metadata,
      taskId: metadata?.taskId,
      localTaskId: metadata?.localTaskId,
      hasMetadata: !!metadata,
      metadataKeys: metadata ? Object.keys(metadata) : [],
      fullMessage: message
    });

    // For confirmation, we need the localTaskId (UUID) if taskId is not a valid numeric ID
    const confirmId = metadata?.localTaskId || metadata?.taskId;

    if (!metadata || !confirmId) {
      console.error('❌ [handleConfirmServiceTask] Service task metadata missing or invalid:', JSON.stringify(metadata, null, 2));
      showInfoDialog(t('chat.cannotCancelServiceTask'), {
        severity: 'error',
        title: t('chat.serviceTask'),
      });
      return;
    }

    const taskType = metadata.taskType || t('chat.serviceTask');
    const confirmTask = await requestConfirmation({
      title: t('chat.confirm'),
      message: `${taskType} ${t('chat.confirmActivity')}`,
      confirmLabel: t('chat.confirm'),
      cancelLabel: t('login.cancel'),
    });
    if (!confirmTask) {
      console.log('🚫 [handleConfirmServiceTask] User cancelled the confirmation');
      return;
    }

    console.log(`📤 [handleConfirmServiceTask] Confirming service task with ID: ${confirmId} (localTaskId: ${metadata.localTaskId}, taskId: ${metadata.taskId})`);

    try {
      // Call backend to confirm the service task (send to GSC API)
      // Use localTaskId (UUID) for new tasks, or taskId for old tasks
      console.log(`📡 [handleConfirmServiceTask] Calling POST /gsc-api/service-tasks/${confirmId}/confirm`);
      const response = await api.post(`/gsc-api/service-tasks/${confirmId}/confirm`);
      console.log('✅ [handleConfirmServiceTask] Confirm API response:', response.data);

      // Update the message to mark as confirmed
      const updatedMetadata: ServiceTaskMetadata = {
        ...metadata,
        status: 'completed', // Service tasks use 'completed' status when confirmed
        taskId: response.data?.gscTaskId || metadata.taskId, // Update with GSC task ID if available
      };

      // Update message in our database
      console.log(`💾 [handleConfirmServiceTask] Updating message ${message.id} in database`);
      await api.put(`/chats/${id}/messages/${message.id}`, {
        metadata: updatedMetadata,
      });

      // Update message locally
      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id
            ? { ...m, metadata: updatedMetadata }
            : m
        )
      );

      console.log('✅ [handleConfirmServiceTask] Service task confirmed successfully');
      showInfoDialog(`${taskType} servis görevi başarıyla onaylandı ve PMS sistemine gönderildi.`, {
        severity: 'success',
        title: 'İşlem Başarılı',
      });
    } catch (error: any) {
      console.error('❌ [handleConfirmServiceTask] Error confirming service task:', error);
      console.error('❌ [handleConfirmServiceTask] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });

      const errorMessage = error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Servis görevi onaylanırken bir hata oluştu.';

      setError(errorMessage);
      showInfoDialog(`${t('chat.failedToCreateServiceTask')}: ${errorMessage}`, {
        severity: 'error',
        title: t('chat.serviceTask'),
      });
    }
  };

  // Cancel activity reservation
  const handleCancelActivity = async (message: Message) => {
    const metadata = message.metadata as ActivityReservationMetadata;
    console.log('🔍 [handleCancelActivity] Checking activity metadata:', {
      metadata,
      activityId: metadata?.activityId,
      activityName: metadata?.activityName,
      hasMetadata: !!metadata,
      metadataKeys: metadata ? Object.keys(metadata) : [],
      fullMessage: message
    });

    if (!metadata || !metadata.activityId) {
      console.error('❌ [handleCancelActivity] Activity metadata missing or invalid:', JSON.stringify(metadata, null, 2));
      console.error('❌ [handleCancelActivity] Full message:', JSON.stringify(message, null, 2));

      const wasCreatedBeforeFix = metadata && !metadata.activityId;
      const errorMsg = wasCreatedBeforeFix
        ? 'Bu aktivite daha önce oluşturulmuş ve PMS ID kaydedilmemiş.\n\n' +
        'Aktivite kartı sohbette görünüyor ancak PMS sistemindeki ID kayıtlı değil.\n\n' +
        'Bu aktiviteyi iptal etmek için:\n' +
        '1. Bu mesajı silin ve yeni bir aktivite oluşturun, VEYA\n' +
        '2. Doğrudan PMS sisteminden iptal edin'
        : 'Aktivite ID bulunamadı. Lütfen konsolu kontrol edin.';

      showInfoDialog('Aktivite iptal edilemedi: ' + errorMsg, {
        severity: 'error',
        title: 'Aktivite Hatası',
      });
      return;
    }

    const activityName = metadata.activityName || 'Aktivite';
    const confirmCancel = await requestConfirmation({
      title: 'Aktiviteyi İptal Et',
      message: `${activityName} aktivitesini iptal etmek istediğinizden emin misiniz?`,
      confirmLabel: 'Evet, İptal Et',
      cancelLabel: 'Vazgeç',
    });
    if (!confirmCancel) {
      console.log('🚫 [handleCancelActivity] User cancelled the confirmation');
      return;
    }

    console.log(`📤 [handleCancelActivity] Cancelling activity track ID: ${metadata.activityId}`);

    try {
      // Call PMS API to cancel the activity
      console.log(`📡 [handleCancelActivity] Calling DELETE /gsc-api/activity-tracks/${metadata.activityId}`);
      const response = await api.delete(`/gsc-api/activity-tracks/${metadata.activityId}`);
      console.log('✅ [handleCancelActivity] PMS API response:', response.data);

      // Update the message to mark as cancelled
      const updatedMetadata: ActivityReservationMetadata = {
        ...metadata,
        status: 'cancelled',
      };

      // Update message in our database
      console.log(`💾 [handleCancelActivity] Updating message ${message.id} in database`);
      await api.put(`/chats/${id}/messages/${message.id}`, {
        metadata: updatedMetadata,
      });

      // Update message locally
      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id
            ? { ...m, metadata: updatedMetadata }
            : m
        )
      );

      console.log('✅ [handleCancelActivity] Activity cancelled successfully');
      showInfoDialog(`${activityName} aktivitesi başarıyla iptal edildi.`, {
        severity: 'success',
        title: 'İşlem Başarılı',
      });
    } catch (error: any) {
      console.error('❌ [handleCancelActivity] Error cancelling activity:', error);
      console.error('❌ [handleCancelActivity] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });

      const errorMessage = error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Aktivite iptal edilirken bir hata oluştu.';

      setError(errorMessage);
      showInfoDialog(`Aktivite iptal edilemedi: ${errorMessage}`, {
        severity: 'error',
        title: 'Aktivite Hatası',
      });
    }
  };

  // Edit service task
  const handleEditServiceTask = (message: Message) => {
    const metadata = message.metadata as ServiceTaskMetadata;
    if (!metadata) return;

    // Pre-fill the form with existing data
    // Note: We need to find the department and group from the task type
    // For now, we'll just set the type and let user reselect if needed
    setServiceTaskType(metadata.taskType || '');
    setServiceTaskDepartment(''); // Will need to be determined from task type
    setServiceTaskGroup(''); // Will need to be determined from task type
    setServiceLocalityType(metadata.localityType);
    setServiceLocality(metadata.locality);
    setServicePriority(metadata.priority);
    setServiceNotes(metadata.notes);
    setServiceDueDate(metadata.dueDate || '');

    // Set the message as selected for updating
    setSelectedMessage(message);
    setServiceTaskModalOpen(true);
  };

  // Cancel service task
  // Enhance service note with AI
  const handleEnhanceServiceNote = async () => {
    if (!selectedMessage || !selectedMessage.content) {
      showInfoDialog(t('chat.noMessageContent'), {
        severity: 'warning',
        title: t('chat.serviceTask'),
      });
      return;
    }

    try {
      setIsEnhancingServiceNote(true);
      console.log('🤖 [ServiceNote] Enhancing message:', selectedMessage.content);

      const response = await api.post(`/chats/${id}/generate-service-note`, {
        guestMessage: selectedMessage.content,
      });

      console.log('✅ [ServiceNote] Enhanced note received:', response.data.enhancedNote);
      setServiceNotes(response.data.enhancedNote);
    } catch (error: any) {
      console.error('❌ [ServiceNote] Error enhancing:', error);
      showInfoDialog(t('chat.failedToEnhanceNote'), {
        severity: 'error',
        title: t('chat.serviceTask'),
      });
    } finally {
      setIsEnhancingServiceNote(false);
    }
  };

  const handleCancelServiceTask = async (message: Message) => {
    const metadata = message.metadata as ServiceTaskMetadata;
    console.log('🔍 Checking service task metadata:', {
      metadata,
      taskId: metadata?.taskId,
      hasMetadata: !!metadata,
      metadataKeys: metadata ? Object.keys(metadata) : [],
      fullMessage: message
    });

    const hasAnyId = metadata?.taskId || metadata?.localTaskId;
    if (!metadata || !hasAnyId) {
      console.error('❌ Service Task metadata:', JSON.stringify(metadata, null, 2));
      console.error('❌ Full message:', JSON.stringify(message, null, 2));

      const wasCreatedBeforeFix = metadata && !metadata.taskId && !metadata.localTaskId;
      const errorMsg = wasCreatedBeforeFix
        ? 'This service task was created before the fix.\n\n' +
        'The task card exists in the chat but the PMS ID was not saved.\n\n' +
        'To cancel this task:\n' +
        '1. Delete this message and create a new task, OR\n' +
        '2. Update/cancel it directly in the PMS system'
        : 'Service Task ID not found. Please check the console for details.';

      showInfoDialog(t('chat.cannotCancelServiceTask') + ': ' + errorMsg, {
        severity: 'warning',
        title: t('chat.serviceTask'),
      });
      return;
    }

    const confirmCancel = await requestConfirmation({
      title: t('chat.serviceTask'),
      message: `${t('chat.confirmCancelServiceTask')}\n\n${t('chat.serviceTaskCancelPmsNote')}`,
      confirmLabel: t('chat.confirm'),
      cancelLabel: t('login.cancel'),
    });
    if (!confirmCancel) {
      return;
    }

    try {
      const isGuest = user?.role === 'guest';

      if (isGuest) {
        const response = await api.post(`/chats/${id}/service-tasks/${message.id}/cancel`);
        const updatedMessage = response.data?.message;
        const feedbackMessage = response.data?.feedbackMessage;
        setMessages((prev) => {
          const next = prev.map((m) =>
            m.id === message.id && updatedMessage
              ? { ...m, metadata: updatedMessage.metadata }
              : m.id === message.id
                ? { ...m, metadata: { ...metadata, status: 'cancelled' } }
                : m,
          );
          if (feedbackMessage) {
            return [...next, feedbackMessage];
          }
          return next;
        });
        return;
      }

      const updatedMetadata: ServiceTaskMetadata = {
        ...metadata,
        status: 'cancelled',
      };

      await api.put(`/chats/${id}/messages/${message.id}`, {
        metadata: updatedMetadata,
      });

      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id
            ? { ...m, metadata: updatedMetadata }
            : m
        )
      );

      showInfoDialog(`${t('chat.serviceTaskCancelled')}\n\n${t('chat.serviceTaskCancelPmsNote')}`, {
        severity: 'info',
        title: t('chat.serviceTask'),
      });
    } catch (error: any) {
      console.error('Error cancelling service task:', error);
      setError(error?.response?.data?.message || 'Failed to cancel service task');
    }
  };

  const handleServiceTaskSubmit = async () => {
    if (!chat || !selectedMessage) return;

    // Check if we're editing an existing service task
    const isEditing = selectedMessage.type === MessageType.SERVICE_TASK;
    const existingMetadata = isEditing ? (selectedMessage.metadata as ServiceTaskMetadata) : null;

    // Get guest user from the selected message or use existing metadata when editing
    let guestUser = selectedMessage.sender;
    let guestName = '';

    if (isEditing && existingMetadata) {
      // When editing, use guest name from metadata
      guestName = existingMetadata.guestName;
    } else {
      // When creating, verify we have a guest user
      if (!guestUser || guestUser.role !== 'guest') {
        setError('Cannot create service task: Guest user not found');
        return;
      }
      // At this point guestUser is guaranteed to be defined due to the check above
      guestName = `${guestUser!.firstName} ${guestUser!.lastName}`;
    }

    const localityInput = serviceLocality || chat.roomId || guestUser?.roomId || '';
    if (!localityInput) {
      setError('Please provide a locality (room number, location, etc.)');
      return;
    }

    try {
      // When using the dropdown, serviceLocality is already the PMS locality ID
      // We use it directly without database lookup
      let localityId = localityInput;
      let roomNumberDisplay = localityInput;

      console.log('📍 Using locality:', {
        type: serviceLocalityType,
        input: localityInput,
        fromDropdown: !!serviceLocality
      });

      let taskResponse;
      let localTaskId: string | undefined = existingMetadata?.localTaskId;
      let finalNumericTaskId: number | undefined;
      let metadataStatus: ServiceTaskMetadata['status'] = isEditing
        ? existingMetadata?.status || 'pending'
        : 'pending';

      if (isEditing && existingMetadata) {
        // When editing, check if we have a valid numeric PMS task ID
        const rawTaskId = existingMetadata.taskId;
        const numericTaskId =
          typeof rawTaskId === 'number'
            ? rawTaskId
            : rawTaskId ? parseInt(String(rawTaskId).trim(), 10) : NaN;

        if (numericTaskId && !Number.isNaN(numericTaskId)) {
          // We have a valid numeric PMS ID, proceed with update directly
          console.log('✅ Updating service task with valid PMS ID:', numericTaskId);
          taskResponse = await api.put(`/gsc-api/service-tasks/${numericTaskId}`, {
            TaskTypeID: serviceTaskType,
            LocalityType: serviceLocalityType,
            LocalityID: localityId,
            Priority: servicePriority,
            DueOn: serviceDueDate || undefined,
            Notes: serviceNotes,
          });
          finalNumericTaskId = numericTaskId;
        } else if (existingMetadata.localTaskId && isValidUUID(existingMetadata.localTaskId)) {
          // No valid PMS ID yet, but we have a valid UUID localTaskId - confirm first to get PMS ID, then update
          console.log('⚡ Service task not confirmed yet. Auto-confirming before update...', {
            localTaskId: existingMetadata.localTaskId,
          });

          try {
            const confirmResponse = await api.post(`/gsc-api/service-tasks/${existingMetadata.localTaskId}/confirm`);
            console.log('✅ Auto-confirmation response:', confirmResponse.data);

            const confirmedTaskId = confirmResponse.data?.gscTaskId || confirmResponse.data?.localId;
            if (!confirmedTaskId) {
              throw new Error('Confirmation did not return a task ID');
            }

            const confirmedNumericId = typeof confirmedTaskId === 'number'
              ? confirmedTaskId
              : parseInt(String(confirmedTaskId).trim(), 10);

            if (!confirmedNumericId || Number.isNaN(confirmedNumericId)) {
              throw new Error(`Invalid task ID returned from confirmation: ${confirmedTaskId}`);
            }

            // Now update with the confirmed numeric ID
            console.log('✅ Updating newly confirmed service task:', confirmedNumericId);
            taskResponse = await api.put(`/gsc-api/service-tasks/${confirmedNumericId}`, {
              TaskTypeID: serviceTaskType,
              LocalityType: serviceLocalityType,
              LocalityID: localityId,
              Priority: servicePriority,
              DueOn: serviceDueDate || undefined,
              Notes: serviceNotes,
            });
            finalNumericTaskId = confirmedNumericId;

            // Update status to completed since we just confirmed it
            metadataStatus = 'completed';

          } catch (confirmError: any) {
            console.error('❌ Failed to auto-confirm service task before update:', {
              error: confirmError,
              response: confirmError?.response?.data,
              status: confirmError?.response?.status,
            });

            const errorMessage = confirmError?.response?.data?.message
              || confirmError?.response?.data?.error
              || confirmError?.message
              || 'Failed to confirm service task before updating';

            setError(errorMessage);
            showInfoDialog(`Could not update service task: ${errorMessage}`, {
              severity: 'error',
              title: t('chat.serviceTask'),
            });
            return;
          }
        } else {
          // No valid PMS ID and no valid UUID localTaskId - cannot update
          console.error('❌ Cannot update service task - no valid ID:', {
            rawTaskId,
            localTaskId: existingMetadata.localTaskId,
            existingMetadata,
          });

          const errorMessage =
            'This service task cannot be updated because it was created in an older version and lacks a valid task ID. ' +
            'Please confirm or recreate the task from the PMS interface.';

          setError(errorMessage);
          showInfoDialog(errorMessage, {
            severity: 'warning',
            title: t('chat.serviceTask'),
          });
          return;
        }
      } else {
        // Create new service task in PMS
        // Pass chatId as query parameter to help backend find the reservation
        const chatIdParam = id ? `?chatId=${id}` : '';
        taskResponse = await api.post(`/gsc-api/service-tasks${chatIdParam}`, {
          TaskTypeID: serviceTaskType,
          LocalityType: serviceLocalityType,
          LocalityID: localityId,
          Priority: servicePriority,
          DueOn: serviceDueDate || undefined,
          Notes: serviceNotes,
        });
      }

      localTaskId = taskResponse.data?.localTaskId || undefined;

      // GSC API returns ResultID (often null) but includes the ID in the Message field
      // Example: "Other Fire-Fighting Works Task with ID: '1018' successfully updated"
      let taskId = taskResponse.data?.ResultID || taskResponse.data?.ID;

      // Only try to extract numeric ID from message if we don't have ResultID/ID and no localTaskId
      // (localTaskId means it's a new unconfirmed task, don't try to extract numeric ID from UUID)
      if (!taskId && !localTaskId && taskResponse.data?.Message) {
        const parsedId = extractIdFromMessage(taskResponse.data.Message);
        if (parsedId) {
          taskId = parsedId;
          console.log('✅ Extracted service task ID from message:', parsedId);
        }
      }

      console.log('📝 Service Task API response:', {
        ResultID: taskResponse.data?.ResultID,
        Message: taskResponse.data?.Message,
        ExtractedID: taskId,
        FullResponse: taskResponse.data
      });

      const hasImageAttachment = !!selectedTaskImage;

      // Determine the final task ID (prefer finalNumericTaskId from edit flow, then taskId, then existing)
      let finalTaskId: string | number | undefined = finalNumericTaskId || taskId || existingMetadata?.taskId;

      // Auto-confirm tasks with images to obtain a numeric PMS task ID
      if (hasImageAttachment && !isEditing) {
        if (!localTaskId) {
          const errorMessage = 'Service task created but missing local ID for confirmation. Image could not be linked.';
          console.error('❌ Missing localTaskId for auto-confirmation');
          setError(errorMessage);
          showInfoDialog(errorMessage, { severity: 'warning', title: 'Servis Görevi' });
          return;
        }

        try {
          console.log('⚡ Auto-confirming service task before linking image:', { localTaskId });
          const confirmResponse = await api.post(`/gsc-api/service-tasks/${localTaskId}/confirm`);
          console.log('✅ Auto-confirmation response:', confirmResponse.data);

          metadataStatus = 'completed';
          finalTaskId = confirmResponse.data?.gscTaskId || confirmResponse.data?.localId || localTaskId;
          taskId = finalTaskId;
        } catch (confirmError: any) {
          console.error('❌ Failed to auto-confirm service task before linking image:', {
            error: confirmError,
            response: confirmError?.response?.data,
            status: confirmError?.response?.status,
          });

          const errorMessage = confirmError?.response?.data?.message
            || confirmError?.response?.data?.error
            || confirmError?.message
            || 'Service task could not be confirmed, so the image was not linked.';

          setError(errorMessage);
          showInfoDialog(errorMessage, { severity: 'error', title: 'Servis Görevi' });
          return;
        }
      }

      // If an image was selected and we created a new task, link it
      let imageUrl: string | undefined = undefined;
      if (finalTaskId && selectedTaskImage && !isEditing) {
        try {
          // Extract filename from image URL
          const fullImageUrl = selectedTaskImage.content;
          const filename = fullImageUrl.split('/').pop();

          if (filename) {
            console.log('🖼️ Converting image to base64:', { filename, taskId });

            // Convert image to base64
            const base64Response = await api.post('/upload/convert-to-base64', {
              filename
            });

            if (!base64Response.data?.base64) {
              throw new Error('Failed to get base64 string from conversion');
            }

            console.log('📤 Linking image to service task:', {
              taskId: finalTaskId,
              base64Length: base64Response.data.base64.length
            });

            // Ensure taskId is a number for the API
            const numericTaskId = typeof finalTaskId === 'string' ? parseInt(finalTaskId, 10) : finalTaskId;

            if (isNaN(numericTaskId)) {
              throw new Error(`Invalid taskId: ${finalTaskId}`);
            }

            // Link image to service task in PMS
            await api.post(`/gsc-api/service-tasks/${numericTaskId}/link-image`, {
              imageString: base64Response.data.base64
            });

            // Store image URL for display in metadata
            imageUrl = fullImageUrl;

            console.log('✅ Image linked to service task successfully!');
          } else {
            console.warn('⚠️ Could not extract filename from image URL:', fullImageUrl);
          }
        } catch (imageError: any) {
          console.error('❌ Failed to link image to service task:', {
            error: imageError,
            message: imageError?.message,
            response: imageError?.response?.data,
            status: imageError?.response?.status,
            taskId: finalTaskId,
            hasImage: !!selectedTaskImage
          });
          // Don't fail the whole operation if image linking fails
          const errorMessage = imageError?.response?.data?.message
            || imageError?.response?.data?.error
            || imageError?.message
            || 'Unknown error';
          const friendlyMessage = 'Service task was created, but the PMS is currently unable to link the image. You can add it later from the PMS interface.';
          setError(friendlyMessage);
          showInfoDialog(`${friendlyMessage}\n\nDetails: ${errorMessage}`, {
            severity: 'warning',
            title: 'Servis Görevi',
          });
        }
      }

      // Create service task metadata
      // Note: GSC API returns ResultID for create/update operations
      const metadata: ServiceTaskMetadata = {
        taskId: finalTaskId || existingMetadata?.taskId,
        localTaskId: localTaskId || existingMetadata?.localTaskId, // Store UUID for confirmation
        guestName: guestName,
        taskType: serviceTaskType.toString(),
        localityType: serviceLocalityType,
        locality: roomNumberDisplay,
        priority: servicePriority as 'High' | 'Normal' | 'Low',
        notes: serviceNotes,
        dueDate: serviceDueDate || undefined,
        status: metadataStatus,
        imageUrl: imageUrl || existingMetadata?.imageUrl,
      };

      if (isEditing && selectedMessage) {
        // Update existing message metadata; for new tasks, backend already creates SERVICE_TASK chat message.
        await api.put(`/chats/${id}/messages/${selectedMessage.id}`, {
          metadata: metadata,
        });
      }

      setServiceTaskModalOpen(false);
      setServiceNotes('');
      setServiceTaskDepartment('');
      setServiceTaskGroup('');
      setServiceTaskType('');
      setServiceLocality('');
      setServiceDueDate('');
      setSelectedTaskImage(null);
      setSelectedMessage(null);
      setError(null);
      showInfoDialog(isEditing ? 'Service task updated successfully!' : 'Service task created successfully!', {
        severity: 'success',
        title: 'İşlem Başarılı',
      });
    } catch (error: any) {
      console.error('Error creating service task:', error);
      setError(error?.response?.data?.message || 'Failed to create service task');
    }
  };

  const handleToggleAI = async () => {
    try {
      // Call the toggle-ai endpoint
      const response = await api.post(`/chats/${id}/toggle-ai`);

      const newAiStatus = response.data.aiEnabled;

      // Update local state from response
      setIsAIEnabled(newAiStatus);
      if (chat) {
        setChat({ ...chat, aiEnabled: newAiStatus });
      }

      // System message will be automatically added by backend
      // Refresh messages to get the system message
      fetchMessages();

      // If AI was just enabled, show typing indicator
      // Backend will automatically respond to last guest message
      if (newAiStatus && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];

        // Check if last message is from a guest (not operator/admin, not LLM, not system)
        const isFromGuest = lastMessage.sender?.role === 'guest' &&
          !lastMessage.isFromLLM &&
          lastMessage.type !== MessageType.SYSTEM;

        if (isFromGuest) {
          // Show typing indicator while waiting for backend response
          setIsLLMTyping(true);
        }
      } else {
        // If AI was disabled, hide typing indicator
        setIsLLMTyping(false);
      }

      // Show a system message (for immediate feedback)
      const systemMessage: Message = {
        id: `system-${Date.now()}`,
        content: newAiStatus ? t('chat.aiAssistantEnabled') : t('chat.aiAssistantDisabled'),
        type: MessageType.SYSTEM,
        chatId: id!,
        senderId: user!.id,
        createdAt: new Date().toISOString(),
        isFromLLM: false,
      };
      setMessages((prev) => [...prev, systemMessage]);
    } catch (error: any) {
      console.error('Error toggling AI:', error);
      // Check if it's a staff chat error
      const errorMessage = error?.response?.data?.message || '';
      if (errorMessage.includes('staff') || errorMessage.includes('admin-only') || errorMessage.includes('admin chat')) {
        setError(t('chat.aiCannotEnableStaff'));
      } else {
        setError(errorMessage || t('chat.aiCannotEnableStaff'));
      }
    }
  };

  // --- Display Helpers ---
  const getMessageAvatar = (message: Message) => {
    if (message.isFromLLM) {
      return <SmartToyIcon sx={{ fontSize: 20, color: 'white' }} />;
    }
    return message.sender?.firstName?.charAt(0) || 'U';
  };

  const isOwnMessage = (message: Message) => {
    return message.senderId === user?.id;
  };

  // --- Rendering ---
  if (isLoading || !chat) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('chat.loadingChat')}</Typography>
      </Box>
    );
  }

  const borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0';
  const cardBg = theme.palette.background.paper;
  const glassBg = isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.6)';
  const glassBorder = isDark ? 'rgba(148,163,184,0.2)' : 'rgba(0, 0, 0, 0.08)';

  // Get guest full name from chat
  const getGuestFullName = () => {
    // Try to get from chat name (format: "FirstName LastName - ...")
    const nameParts = chat.name.split('-');
    if (nameParts.length > 0) {
      return nameParts[0].trim() || 'Guest User';
    }
    return 'Guest User';
  };

  const guestFullName = getGuestFullName();
  const reservationId = chat.reservationId || 'N/A';

  // Pull a representative guest user from chat data so the operator can hover the
  // "?" icon next to the chat title and inspect the contact details for whoever
  // they are talking to (matches the conversation participants where possible).
  const chatGuestUsers = ((chat as any).room?.guests || []).filter((u: any) => u?.role === 'guest');
  const messageGuestSender = messages.find(m => m.sender?.role === 'guest')?.sender as any;
  const primaryGuest = (() => {
    if (messageGuestSender) return messageGuestSender;
    const expectedFirstName = guestFullName.split(' ')[0]?.toLowerCase();
    const matchedByName = chatGuestUsers.find(
      (g: any) => g?.firstName?.toLowerCase() === expectedFirstName,
    );
    return matchedByName || chatGuestUsers[0] || null;
  })();
  const guestDetails = {
    ...(primaryGuest || {}),
    ...(detailedGuestProfile || {}),
  } as any;
  const guestNationality = guestDetails?.nationality || guestDetails?.Nationality || '';
  const guestLanguage = guestDetails?.language || guestDetails?.Language || guestDetails?.preferredLanguage || guestNationality;
  const guestCountryCodeRaw =
    guestDetails?.countryCode
    || guestDetails?.CountryCode
    || guestDetails?.country
    || guestDetails?.Country
    || guestDetails?.Address?.Country
    || '';
  const guestCountryCode = String(guestCountryCodeRaw).trim().toUpperCase();
  const guestLanguageFlag = getFlagEmoji(guestCountryCode);
  const guestPhone = guestDetails?.phoneNumber || guestDetails?.Phone || guestDetails?.PhoneNumber || '';
  const guestPassport = guestDetails?.passportId || guestDetails?.PassportID || guestDetails?.PassportId || '';
  const guestNationalId = guestDetails?.nationalId || guestDetails?.NationalID || guestDetails?.IdentityNo || '';
  const displayGuestId = guestPassport || guestNationalId;

  // Get room number if available (hide for virtual room 000 - guests without physical room)
  const getRoomNumber = () => {
    if (chat.room?.roomNumber && chat.room.roomNumber !== '000') {
      return chat.room.roomNumber;
    }
    // Try to extract from chat name if it contains room number (but not 000)
    const roomMatch = chat.name.match(/Room\s+(\d+)/i);
    if (roomMatch && roomMatch[1] !== '000') {
      return roomMatch[1];
    }
    return null;
  };

  const roomNumber = getRoomNumber();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isDark ? theme.palette.background.default : '#EEF2F7',
      }}
    >
      <Paper
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRadius: { xs: 0, md: 6 },
          bgcolor: isDark ? theme.palette.background.default : '#EEF2F7',
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        {/* Chat Header */}
        <Box sx={{
          p: { xs: 'calc(env(safe-area-inset-top, 0px) + 12px) 14px 12px', md: 1.5 },
          borderBottom: `1px solid ${isDark ? theme.palette.divider : '#E4E9F1'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            <IconButton
              edge="start"
              onClick={() => {
                // Navigate back to dashboard and restore the previous filter
                // First try to get from location state, then from localStorage, fallback to 'person'
                const previousFilter = (location.state as any)?.previousFilter ||
                  localStorage.getItem('chatFilter') ||
                  'person';
                navigate('/dashboard', { state: { filter: previousFilter } });
              }}
              sx={{
                mr: 1,
                display: { xs: 'flex', md: 'none' },
                width: 42,
                height: 42,
                borderRadius: '12px',
                bgcolor: isDark ? theme.palette.action.hover : '#F1F5FA',
                color: '#15294D',
                '&:hover': {
                  backgroundColor: isDark ? theme.palette.action.selected : '#E3EBF5',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                fontSize: '13.5px',
                fontWeight: 800,
                background: 'linear-gradient(135deg,#B37BF0,#9B5CE0)',
                mr: 1.25,
                display: { xs: 'flex', md: 'none' },
              }}
            >
              {(guestFullName || '?').split(' ').map((p: string) => p[0]).join('').slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: isDark ? '#e2e8f0' : '#15294D',
                    fontSize: '15.5px',
                    lineHeight: 1.2,
                  }}
                >
                  {guestFullName}
                </Typography>
                {(primaryGuest || guestFullName) && (
                  <Tooltip
                    enterDelay={150}
                    leaveDelay={150}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 320,
                          p: 0,
                          bgcolor: 'transparent',
                        },
                      },
                    }}
                    title={
                      <Box
                        sx={{
                          p: 1.75,
                          borderRadius: 2,
                          bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          boxShadow: isDark
                            ? '0 12px 32px rgba(0,0,0,0.55)'
                            : '0 12px 32px rgba(15, 23, 42, 0.18)',
                          border: isDark
                            ? '1px solid rgba(148,163,184,0.25)'
                            : '1px solid rgba(0,0,0,0.06)',
                          minWidth: 240,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: '#9B5CE0',
                              fontSize: '0.95rem',
                              fontWeight: 700,
                            }}
                          >
                            {(guestDetails?.firstName || guestDetails?.FirstName || guestFullName)?.charAt(0)?.toUpperCase() || '?'}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                              {primaryGuest
                                ? `${guestDetails?.firstName || guestDetails?.FirstName || ''} ${guestDetails?.lastName || guestDetails?.LastName || ''}`.trim()
                                : guestFullName}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                              {t('chat.guest')}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.8125rem' }}>
                          {(guestDetails?.email || guestDetails?.Email) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <EmailIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              <Typography sx={{ fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                                {guestDetails?.email || guestDetails?.Email}
                              </Typography>
                            </Box>
                          )}
                          {guestPhone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <PhoneIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              <Typography sx={{ fontSize: '0.8125rem' }}>
                                {maskPhone(guestPhone)}
                              </Typography>
                            </Box>
                          )}
                          {(guestDetails?.birthdate || guestDetails?.Birthdate || guestDetails?.DateOfBirth) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <CakeIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              <Typography sx={{ fontSize: '0.8125rem' }}>
                                {guestDetails?.birthdate || guestDetails?.Birthdate || guestDetails?.DateOfBirth}
                              </Typography>
                            </Box>
                          )}
                          {guestLanguage && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <PublicIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '0.8125rem' }}>
                                  {guestLanguage}
                                  {guestCountryCode ? ` · ${guestCountryCode}` : ''}
                                </Typography>
                                {guestCountryCode && <InlineFlagIcon countryCode={guestCountryCode} size={13} />}
                                {!guestCountryCode && guestLanguageFlag && (
                                  <Typography sx={{ fontSize: '0.8125rem' }}>{guestLanguageFlag}</Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          {displayGuestId && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <BadgeIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              <Typography sx={{ fontSize: '0.8125rem' }}>
                                {displayGuestId}
                              </Typography>
                            </Box>
                          )}
                          {(guestDetails?.city || guestDetails?.City || guestDetails?.Address?.City || guestDetails?.country || guestDetails?.Country || guestDetails?.Address?.Country) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <LocationIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              <Typography sx={{ fontSize: '0.8125rem' }}>
                                {[
                                  guestDetails?.city || guestDetails?.City || guestDetails?.Address?.City,
                                  guestDetails?.country || guestDetails?.Country || guestDetails?.Address?.Country,
                                ].filter(Boolean).join(', ')}
                              </Typography>
                            </Box>
                          )}
                          {roomNumber && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <PersonIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              <Typography sx={{ fontSize: '0.8125rem' }}>
                                {t('chat.room')}: {roomNumber}
                              </Typography>
                            </Box>
                          )}
                          {chat.reservation?.checkInDate && chat.reservation?.checkOutDate && (
                            <Typography sx={{ fontSize: '0.75rem', opacity: 0.7, mt: 0.25 }}>
                              {chat.reservation.checkInDate.split('T')[0]} → {chat.reservation.checkOutDate.split('T')[0]}
                            </Typography>
                          )}

                          {!(guestDetails?.email || guestDetails?.Email)
                            && !guestPhone
                            && !(guestDetails?.birthdate || guestDetails?.Birthdate || guestDetails?.DateOfBirth)
                            && !guestLanguage
                            && !displayGuestId
                            && !(guestDetails?.city || guestDetails?.City || guestDetails?.Address?.City)
                            && !(guestDetails?.country || guestDetails?.Country || guestDetails?.Address?.Country) && (
                              <Typography sx={{ fontSize: '0.8125rem', opacity: 0.7, fontStyle: 'italic' }}>
                                {t('chat.guestInfoUnavailable', {
                                  defaultValue: 'No additional guest information available.',
                                })}
                              </Typography>
                            )}
                        </Box>
                      </Box>
                    }
                  >
                    <IconButton
                      size="small"
                      aria-label={t('chat.guestInfoTooltip', { defaultValue: 'Guest information' }) as string}
                      sx={{
                        width: 17,
                        height: 17,
                        color: '#8A94A8',
                        border: '1.5px solid #B9C2D4',
                        bgcolor: 'transparent',
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(24, 155, 240, 0.08)',
                          color: '#189BF0',
                        },
                      }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography sx={{ color: isDark ? '#cbd5f5' : '#8A94A8', fontWeight: 400, fontSize: '12px', mt: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t('chat.reservationId')}: {reservationId}
                {chat.type === ChatType.ONE_ON_ONE && roomNumber && ` | ${t('chat.room')}: ${roomNumber}`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
            <Chip
              label={chat.type === 'room_chat' ? t('chat.roomChat') : t('chat.generalChat')}
              size="small"
              sx={{
                backgroundColor: isDark ? theme.palette.action.hover : '#EDF1F7',
                color: '#5B6578',
                fontWeight: 700,
                border: 'none',
                height: 28,
                fontSize: '11.5px',
                borderRadius: 999,
              }}
            />

            {/* Admin Only Badge */}
            {chat.isAdminOnly && (
              <Chip
                label={t('chat.adminOnly')}
                size="small"
                sx={{
                  backgroundColor: '#fee2e2',
                  color: '#991b1b',
                  fontWeight: 700,
                  height: 28,
                  fontSize: '11.5px',
                  borderRadius: 999,
                }}
              />
            )}

            {/* Staff Only Badge */}
            {chat.isStaffOnly && (
              <Chip
                label={t('chat.staffChat')}
                size="small"
                sx={{
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  fontWeight: 700,
                  height: 28,
                  fontSize: '11.5px',
                  borderRadius: 999,
                }}
              />
            )}

            {/* AI/Operator Indicator */}
            <Box
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                fontWeight: 700,
                fontSize: '11.5px',
                backgroundColor: isAIEnabled
                  ? '#DFF2FF'
                  : (isDark ? 'rgba(148, 163, 184, 0.2)' : '#EDF1F7'),
                color: isAIEnabled ? '#189BF0' : '#5B6578',
                minWidth: 36,
                textAlign: 'center',
              }}
            >
              {isAIEnabled ? 'AI' : 'OP'}
            </Box>

            {/* Night Mode Indicator - Visible to ALL users (guests and staff) */}
            {isNightModeActive() && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      🌙 {t('chat.nightModeActive')}
                    </Typography>
                    <Typography variant="caption" component="div">
                      {t('chat.nightModeDescription')}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
                      {t('chat.active')}: {hotelSettings?.settings?.nightModeStartTime || '18:00'} - {hotelSettings?.settings?.nightModeEndTime || '08:00'}
                    </Typography>
                  </Box>
                }
                arrow
              >
                <Chip
                  icon={<span style={{ fontSize: '12px' }}>🌙</span>}
                  label={t('chat.nightMode')}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: '#3b82f6',
                    fontWeight: 500,
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    height: 24,
                    fontSize: '0.75rem',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.7 },
                    },
                    '& .MuiChip-icon': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Tooltip>
            )}

            {/* AI Assistant Toggle - Only for operators and admins */}
            {(user?.role === 'operator' || user?.role === 'admin') && (
              <Tooltip
                title={
                  (chat.isAdminOnly || chat.isStaffOnly)
                    ? t('chat.aiCannotEnableStaff')
                    : isNightModeActive() && !chat.aiEnabled
                      ? `🌙 ${t('chat.nightModeActive')} - ${t('chat.aiAssistant')} automatically enabled`
                      : ''
                }
                arrow
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAIEnabled}
                      onChange={handleToggleAI}
                      size="small"
                      disabled={chat.isAdminOnly || chat.isStaffOnly || (isNightModeActive() && !chat.aiEnabled)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#667eea',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#667eea',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isNightModeActive() && !chat.aiEnabled && <span>🌙</span>}
                      <HeadsetMicIcon sx={{ fontSize: 16, color: isAIEnabled ? '#667eea' : '#9ca3af' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: (chat.isAdminOnly || chat.isStaffOnly) ? '#9ca3af' : (isAIEnabled ? '#667eea' : '#6b7280'),
                          fontWeight: isAIEnabled ? 500 : 400,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {t('chat.aiAssistant')} {isNightModeActive() && !chat.aiEnabled ? `(${t('chat.nightMode')})` : ''}
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ m: 0 }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Alert Banner (Status bar) - Only visible for guests; status from stay dates */}
        {user?.role === 'guest' && (() => {
          const checkIn = chat?.reservation?.checkInDate ? new Date(chat.reservation.checkInDate) : null;
          const checkOut = chat?.reservation?.checkOutDate ? new Date(chat.reservation.checkOutDate) : null;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (checkIn) checkIn.setHours(0, 0, 0, 0);
          if (checkOut) checkOut.setHours(0, 0, 0, 0);

          let stayStatusKey = 'chat.inHouse';
          if (checkIn && today < checkIn) stayStatusKey = 'chat.expectedStay';
          else if (checkOut && today > checkOut) stayStatusKey = 'chat.departedStay';
          else if (checkIn && checkOut && today >= checkIn && today <= checkOut) stayStatusKey = 'chat.inHouse';

          return (
          <Box sx={{
            mx: { xs: 1.5, md: 2 },
            mt: 1,
            mb: 0.5,
            bgcolor: isDark ? 'rgba(34,197,94,0.08)' : '#E6F6EC',
            border: `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : '#CBEBD8'}`,
            borderRadius: '10px',
            color: isDark ? '#bbf7d0' : '#2E7D4F',
            p: '9px 12px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '6px 14px',
            fontSize: '12.5px',
          }}>
            <Typography sx={{ fontWeight: 800, color: isDark ? '#86efac' : '#1E9E56', fontSize: '12.5px' }}>
              {t('chat.status')}: {t(stayStatusKey)}
            </Typography>
            {chat?.reservation?.checkInDate && (
              <Typography sx={{ fontWeight: 600, fontSize: '12.5px' }}>
                {t('chat.arrivalDate')}: {new Date(chat.reservation.checkInDate).toLocaleDateString()}
              </Typography>
            )}
            {chat?.reservation?.checkOutDate && (
              <Typography sx={{ fontWeight: 600, fontSize: '12.5px' }}>
                {t('chat.departureDate')}: {new Date(chat.reservation.checkOutDate).toLocaleDateString()}
              </Typography>
            )}
          </Box>
          );
        })()}

        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              m: 2,
                                borderRadius: 16,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Message List Area */}
        <List sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: '16px 14px 10px',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}>
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwn={isOwnMessage(message)}
              getMessageAvatar={getMessageAvatar}
              isOperator={user?.role === 'operator' || user?.role === 'admin'}
              onAction={handleMessageAction}
              onEditActivity={handleEditActivity}
              onCancelActivity={handleCancelActivity}
              onConfirmActivity={handleConfirmActivity}
              onEditServiceTask={handleEditServiceTask}
              onCancelServiceTask={handleCancelServiceTask}
              onConfirmServiceTask={handleConfirmServiceTask}
              onGuestCommentReply={handleGuestCommentReply}
              onImageClick={(imageUrl) => {
                setPreviewImageUrl(imageUrl);
                setImagePreviewOpen(true);
              }}
              currentUserId={user?.id}
              currentProfileId={user?.profileId}
              isGuestUser={user?.role === 'guest'}
              t={t}
            />
          ))}

          {/* LLM Typing Indicator - Only show if AI is enabled */}
          {isLLMTyping && isAIEnabled && (
            <ListItem sx={{ alignItems: 'flex-start', py: 1.5 }}>
              <Avatar sx={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                mx: 1.5,
                width: 36,
                height: 36,
                mt: 0.5,
                fontSize: '0.875rem',
                boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <SmartToyIcon sx={{ fontSize: 20, color: 'white' }} />
              </Avatar>
              <Box
                sx={{
                  bgcolor: 'rgba(219, 234, 254, 0.8)',
                  backdropFilter: 'blur(10px)',
                  p: 2,
                  borderRadius: '16px 16px 16px 8px',
                  maxWidth: '60%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  boxShadow: '0 4px 12px rgba(96, 165, 250, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                  border: '1px solid rgba(96, 165, 250, 0.2)',
                }}
              >
                <CircularProgress size={16} sx={{ color: '#60a5fa' }} />
                <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 500 }}>
                  {t('chat.aiThinking')}
                </Typography>
              </Box>
            </ListItem>
          )}

          <div ref={messagesEndRef} />
        </List>

        {/* Image Preview Section */}
        {imagePreview && (
          <Box sx={{
            p: 2,
            background: glassBg,
            backdropFilter: 'blur(10px)',
            borderTop: `1px solid ${glassBorder}`,
          }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  maxWidth: '100px',
                  maxHeight: '100px',
                  borderRadius: 16,
                  objectFit: 'cover',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                }}
              />
              <IconButton
                size="small"
                onClick={clearSelectedImage}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  color: '#ef4444',
                  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  '&:hover': {
                    bgcolor: '#ef4444',
                    color: 'white',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Reply Indicator */}
        {replyToMessage && (
          <Box sx={{
            p: 2,
            background: 'rgba(102, 126, 234, 0.08)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(102, 126, 234, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ReplyIcon sx={{ color: '#667eea', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea' }}>
                  {t('chat.replyingTo')} {replyToMessage.sender?.firstName || t('chat.unknown')}
                </Typography>
                <Typography variant="caption" display="block" sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px',
                  color: '#6b7280',
                  mt: 0.5,
                }}>
                  {replyToMessage.content}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={cancelReply}
              sx={{
                color: '#6b7280',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}

        {/* Input Composer */}
        <Box sx={{
          display: 'flex',
          p: { xs: '10px 12px calc(env(safe-area-inset-bottom, 0px) + 12px)', md: 2 },
          background: isDark ? theme.palette.background.default : '#EEF2F7',
          borderTop: 'none',
          alignItems: 'center',
          gap: 1.125,
          flexShrink: 0,
        }}>

          {/* Attachment Button */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="file"
              id="image-upload"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />
            <Tooltip title={t('chat.attachImage')}>
              <IconButton
                onClick={() => document.getElementById('image-upload')?.click()}
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  border: 'none',
                  background: theme.palette.background.paper,
                  color: '#7C879B',
                  boxShadow: '0 1px 4px rgba(21,41,77,.08)',
                  '&:hover': {
                    background: isDark ? theme.palette.action.hover : '#f8fafc',
                  },
                }}
              >
                <AttachFileIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ position: 'relative', flex: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('chat.writeHere')}
              value={newMessage}
              onChange={handleMessageChange}
              onKeyPress={handleKeyPress}
              disabled={isUploadingImage}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: '0 1px 4px rgba(21,41,77,.08)',
                  fontSize: '14px',
                  '& fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                },
              }}
              multiline
              maxRows={4}
            />

            {/* AI Suggestion - Compact & Modern */}
            {(textSuggestion || isFetchingSuggestion) &&
              (user?.role === 'operator' || user?.role === 'admin') &&
              newMessage.trim() !== lastSentMessageRef.current.trim() && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    mb: 1,
                    background: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(6, 182, 212, 0.2)',
                    borderRadius: 6,
                    p: 1.5,
                    boxShadow: isDark
                      ? '0 4px 20px rgba(6, 182, 212, 0.2), 0 0 0 1px rgba(148, 163, 184, 0.1) inset'
                      : '0 4px 20px rgba(6, 182, 212, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                    zIndex: 1000,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, #06b6d4 0%, #0891b2 100%)',
                      borderRadius: '6px 6px 0 0',
                    },
                    animation: 'slideUp 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes slideUp': {
                      from: {
                        opacity: 0,
                        transform: 'translateY(8px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  {/* Header - Compact */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)',
                        }}
                      >
                        <HeadsetMicIcon sx={{ fontSize: 14, color: 'white' }} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: '#06b6d4',
                          fontSize: '0.75rem',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {t('chat.aiSuggestion')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={dismissSuggestion}
                      sx={{
                        color: '#9ca3af',
                        width: 24,
                        height: 24,
                        padding: 0,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          transform: 'rotate(90deg)',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {/* Content - Compact */}
                  {isFetchingSuggestion ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <CircularProgress
                        size={16}
                        thickness={4}
                        sx={{ color: '#06b6d4' }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                          fontWeight: 500,
                          fontStyle: 'italic',
                          fontSize: '0.75rem',
                        }}
                      >
                        {t('chat.thinking')}...
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        fontWeight: 500,
                        lineHeight: 1.5,
                        mb: 1.5,
                        fontSize: '0.875rem',
                      }}
                    >
                      {textSuggestion}
                    </Typography>
                  )}

                  {/* Action Buttons - Compact */}
                  {textSuggestion && !isFetchingSuggestion && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={dismissSuggestion}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                                borderRadius: 16,
                          py: 0.75,
                          px: 1.5,
                          borderColor: isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(0, 0, 0, 0.12)',
                          color: isDark ? '#cbd5e1' : '#64748b',
                          fontSize: '0.8125rem',
                          backgroundColor: isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: isDark ? '#94a3b8' : '#9ca3af',
                            backgroundColor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        {t('chat.dismiss')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={acceptSuggestion}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          fontWeight: 700,
                                borderRadius: 16,
                          py: 0.75,
                          px: 1.5,
                          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                          boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)',
                          color: 'white',
                          fontSize: '0.8125rem',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                        }}
                      >
                        {t('chat.accept')}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
          </Box>

          <Tooltip title={t('chat.sendMessage')}>
            <IconButton
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && !selectedImage) || isUploadingImage}
              sx={{
                width: 46,
                height: 46,
                borderRadius: '14px',
                background: (newMessage.trim() || selectedImage) && isConnected && !isUploadingImage
                  ? '#189BF0'
                  : (isDark ? theme.palette.action.disabledBackground : '#E4E9F1'),
                color: (newMessage.trim() || selectedImage) && isConnected && !isUploadingImage ? '#fff' : '#94a3b8',
                boxShadow: (newMessage.trim() || selectedImage) && isConnected && !isUploadingImage
                  ? '0 4px 12px rgba(24,155,240,.35)'
                  : 'none',
                flexShrink: 0,
                '&:hover': {
                  background: (newMessage.trim() || selectedImage) && isConnected && !isUploadingImage
                    ? '#1586d0'
                    : (isDark ? theme.palette.action.hover : '#E4E9F1'),
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
                '&:disabled': {
                  color: '#94a3b8',
                },
              }}
            >
              {isUploadingImage ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Guest Comment Modal */}
        <Dialog
          open={commentModalOpen}
          onClose={() => {
            setCommentModalOpen(false);
            resetGuestCommentForm();
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
                                borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
              border: '1px solid rgba(255, 255, 255, 0.18)',
            },
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Make a Guest Comment
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Department Filter */}
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel
                    sx={{
                      fontWeight: 500,
                      color: isDark ? theme.palette.text.secondary : '#64748b',
                      '&.Mui-focused': {
                        color: isDark ? '#3b82f6' : '#667eea',
                        fontWeight: 500,
                      },
                    }}
                  >
                  Department
                </InputLabel>
                <Select
                  value={commentDepartment}
                  label="Department"
                  onChange={(e: SelectChangeEvent) => {
                    setCommentDepartment(e.target.value);
                    setCommentGroup(''); // Reset group when department changes
                    setCommentType(''); // Reset type when department changes
                  }}
                  disabled={configLoading || !guestCommentConfig?.Departments?.length}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : guestCommentConfig?.Departments?.length ? (
                    guestCommentConfig.Departments.map((dept) => (
                      <MenuItem key={dept.ID} value={dept.ID}>
                        {dept.Code} - {dept.Description}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No departments available</MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Group Filter - filtered by Department */}
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel
                    sx={{
                      fontWeight: 500,
                      color: isDark ? theme.palette.text.secondary : '#64748b',
                      '&.Mui-focused': {
                        color: isDark ? '#3b82f6' : '#667eea',
                        fontWeight: 500,
                      },
                    }}
                  >
                  Group
                </InputLabel>
                <Select
                  value={commentGroup}
                  label="Group"
                  onChange={(e: SelectChangeEvent) => {
                    setCommentGroup(e.target.value);
                    setCommentType(''); // Reset type when group changes
                  }}
                  disabled={configLoading || !commentDepartment || !guestCommentConfig?.Groups?.length}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : (() => {
                    const filteredGroups = guestCommentConfig?.Groups?.filter(
                      (group) => group.Department?.ID === commentDepartment
                    ) || [];
                    return filteredGroups.length ? (
                      filteredGroups.map((group) => (
                        <MenuItem key={group.ID} value={group.ID}>
                          {group.Code} - {group.Description}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No groups available for selected department</MenuItem>
                    );
                  })()}
                </Select>
              </FormControl>

              {/* Type Filter - filtered by Group */}
              <FormControl fullWidth sx={textFieldSx}>
              <InputLabel
                  sx={{
                    fontWeight: 500,
                    color: isDark ? theme.palette.text.secondary : '#64748b',
                    '&.Mui-focused': {
                      color: isDark ? '#3b82f6' : '#667eea',
                      fontWeight: 500,
                    },
                  }}
                >
                  Type
                </InputLabel>
                <Select
                  value={commentType}
                  label="Type"
                  onChange={(e: SelectChangeEvent) => setCommentType(e.target.value)}
                  disabled={configLoading || !commentGroup || !guestCommentConfig?.Types?.length}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : (() => {
                    const filteredTypes = guestCommentConfig?.Types?.filter(
                      (type) => type.Group?.ID === commentGroup
                    ) || [];
                    return filteredTypes.length ? (
                      filteredTypes.map((type) => (
                        <MenuItem key={type.ID} value={type.ID}>
                          {type.Code} - {type.Description}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No types available for selected group</MenuItem>
                    );
                  })()}
                </Select>
              </FormControl>

              {/* Comment Text */}
              <TextField
                label="Comment Text"
                multiline
                rows={4}
                fullWidth
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                sx={textFieldSx}
              />

              {/* Status */}
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel
                  sx={{
                    fontWeight: 500,
                    color: isDark ? theme.palette.text.secondary : '#64748b',
                    '&.Mui-focused': {
                      color: isDark ? '#3b82f6' : '#667eea',
                      fontWeight: 500,
                    },
                  }}
                >
                  Status
                </InputLabel>
                <Select
                  value={commentStatus}
                  label="Status"
                  onChange={(e: SelectChangeEvent) => setCommentStatus(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                      },
                    },
                  }}
                >
                  <MenuItem value="Positive">Positive</MenuItem>
                  <MenuItem value="Neutral">Neutral</MenuItem>
                  <MenuItem value="Negative">Negative</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={() => {
                setCommentModalOpen(false);
                resetGuestCommentForm();
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                                borderRadius: 16,
                px: 3,
                color: '#6b7280',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGuestCommentSubmit}
              variant="contained"
              disabled={!commentType}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                                borderRadius: 16,
                px: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
                  boxShadow: 'none',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Save Comment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activity Reservation Modal */}
        <Dialog
          open={activityModalOpen}
          onClose={() => setActivityModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
                                borderRadius: 8,
              background: isDark
                ? 'rgba(15, 23, 42, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: isDark
                ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(148, 163, 184, 0.1) inset'
                : '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
              border: isDark
                ? '1px solid rgba(148, 163, 184, 0.2)'
                : '1px solid rgba(255, 255, 255, 0.18)',
            },
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            background: isDark
              ? 'linear-gradient(135deg, #818cf8 0%, #a78bfa 50%, #f472b6 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            pb: 2,
          }}>
            {selectedMessage?.type === MessageType.ACTIVITY_RESERVATION ? 'Edit Activity Reservation' : 'New Activity Reservation'}
          </DialogTitle>
          <DialogContent sx={{
            bgcolor: 'transparent',
            '& .MuiFormHelperText-root': {
              color: isDark ? 'rgba(203, 213, 225, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            },
          }}>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Guest Name"
                fullWidth
                value={activityName}
                onChange={(e) => setActivityName(e.target.value)}
                helperText="Name of the guest making the reservation"
                sx={textFieldSx}
                InputLabelProps={{
                  sx: {
                    color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  },
                }}
              />
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel sx={{
                  color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  '&.Mui-focused': {
                    color: isDark ? '#818cf8' : '#667eea',
                  },
                }}>
                  Activity Type
                </InputLabel>
                <Select
                  value={activityTypeId}
                  label="Activity Type"
                  onChange={(e: SelectChangeEvent) => {
                    const typeId = String(e.target.value ?? '');
                    setActivityTypeId(typeId);
                    const selectedType = activityTypes.find(t => String(t.ID) === typeId);
                    setSelectedActivityType(selectedType || null);
                    setActivitySession(''); // Reset session when activity type changes
                    setShuttleOutboundSession('');
                    setShuttleReturnSession('');
                  }}
                  disabled={configLoading || !activityTypes?.length}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(129, 140, 248, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(129, 140, 248, 0.3)' : 'rgba(102, 126, 234, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : activityTypes?.length ? (
                    activityTypes.map((type) => (
                      <MenuItem key={String(type.ID)} value={String(type.ID)}>
                        {type.Code} - {type.Description}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No activity types available</MenuItem>
                  )}
                </Select>
              </FormControl>
              <TextField
                label="Date"
                type="date"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  },
                }}
                inputProps={{
                  min: chat?.reservation?.checkInDate?.split('T')[0],
                  max: chat?.reservation?.checkOutDate?.split('T')[0],
                  sx: {
                    color: isDark ? '#e2e8f0' : undefined,
                  },
                }}
                value={activityDate}
                onChange={(e) => setActivityDate(e.target.value)}
                error={!!(activityDate && selectedActivityType?.ActiveDays?.length && !selectedActivityType.ActiveDays.includes(getDayNameForDate(activityDate)))}
                helperText={(() => {
                  const range = chat?.reservation
                    ? `Select date between ${chat.reservation.checkInDate?.split('T')[0]} and ${chat.reservation.checkOutDate?.split('T')[0]}`
                    : 'Select activity date';
                  const activeDays = selectedActivityType?.ActiveDays?.length
                    ? ` This activity is only available on: ${selectedActivityType.ActiveDays.join(', ')}.`
                    : '';
                  const invalidDay = activityDate && selectedActivityType?.ActiveDays?.length && !selectedActivityType.ActiveDays.includes(getDayNameForDate(activityDate))
                    ? ` ${getDayNameForDate(activityDate)} is not available for this activity.`
                    : '';
                  return range + activeDays + invalidDay;
                })()}
                sx={textFieldSx}
              />
              {selectedActivityType?.Sessions && selectedActivityType.Sessions.length > 0 && (
                selectedActivityType.InternalType === 'ShuttleReservation' ? (
                  // Shuttle: show outbound (ToDestination) and return (FromDestination) on separate rows.
                  // Neither is mandatory: a guest may need only one direction.
                  <>
                    <FormControl fullWidth sx={textFieldSx}>
                      <InputLabel sx={{
                        color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                        '&.Mui-focused': {
                          color: isDark ? '#818cf8' : '#667eea',
                        },
                      }}>
                        {t('chat.shuttleOutbound') || 'Outbound (Hotel → Destination)'}
                      </InputLabel>
                      <Select
                        value={shuttleOutboundSession}
                        label={t('chat.shuttleOutbound') || 'Outbound (Hotel → Destination)'}
                        onChange={(e: SelectChangeEvent) => setShuttleOutboundSession(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: 6,
                              mt: 1,
                              bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                              boxShadow: isDark
                                ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                                : '0 8px 32px rgba(0, 0, 0, 0.12)',
                              border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                              '& .MuiMenuItem-root': {
                                color: isDark ? '#e2e8f0' : '#1f2937',
                                '&:hover': {
                                  bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                },
                                '&.Mui-selected': {
                                  bgcolor: isDark ? 'rgba(129, 140, 248, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                                  '&:hover': {
                                    bgcolor: isDark ? 'rgba(129, 140, 248, 0.3)' : 'rgba(102, 126, 234, 0.15)',
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('chat.notNeeded') || 'Not needed'}</em>
                        </MenuItem>
                        {selectedActivityType.Sessions
                          .filter((s) => getNormalizedSessionDirection(s.Direction) === 'to')
                          .map((session) => (
                            <MenuItem key={session.ID} value={session.ID}>
                              {session.Code} - {session.Description}
                              {session.Time && ` (${new Date(session.Time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })})`}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={textFieldSx}>
                      <InputLabel sx={{
                        color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                        '&.Mui-focused': {
                          color: isDark ? '#818cf8' : '#667eea',
                        },
                      }}>
                        {t('chat.shuttleReturn') || 'Return (Destination → Hotel)'}
                      </InputLabel>
                      <Select
                        value={shuttleReturnSession}
                        label={t('chat.shuttleReturn') || 'Return (Destination → Hotel)'}
                        onChange={(e: SelectChangeEvent) => setShuttleReturnSession(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: 6,
                              mt: 1,
                              bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                              boxShadow: isDark
                                ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                                : '0 8px 32px rgba(0, 0, 0, 0.12)',
                              border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                              '& .MuiMenuItem-root': {
                                color: isDark ? '#e2e8f0' : '#1f2937',
                                '&:hover': {
                                  bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                },
                                '&.Mui-selected': {
                                  bgcolor: isDark ? 'rgba(129, 140, 248, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                                  '&:hover': {
                                    bgcolor: isDark ? 'rgba(129, 140, 248, 0.3)' : 'rgba(102, 126, 234, 0.15)',
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('chat.notNeeded') || 'Not needed'}</em>
                        </MenuItem>
                        {selectedActivityType.Sessions
                          .filter((s) => getNormalizedSessionDirection(s.Direction) === 'from')
                          .map((session) => (
                            <MenuItem key={session.ID} value={session.ID}>
                              {session.Code} - {session.Description}
                              {session.Time && ` (${new Date(session.Time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })})`}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" sx={{
                      mt: -1,
                      ml: 1,
                      color: isDark ? 'rgba(203, 213, 225, 0.6)' : 'rgba(0, 0, 0, 0.55)',
                    }}>
                      {t('chat.shuttleHint') || 'Either direction is optional — pick only the trip(s) the guest needs.'}
                    </Typography>
                  </>
                ) : (
                  <FormControl fullWidth sx={textFieldSx}>
                    <InputLabel sx={{
                      color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                      '&.Mui-focused': {
                        color: isDark ? '#818cf8' : '#667eea',
                      },
                    }}>
                      Session
                    </InputLabel>
                    <Select
                      value={activitySession}
                      label="Session"
                      onChange={(e: SelectChangeEvent) => setActivitySession(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 6,
                            mt: 1,
                            bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                            boxShadow: isDark
                              ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                              : '0 8px 32px rgba(0, 0, 0, 0.12)',
                            border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                            '& .MuiMenuItem-root': {
                              color: isDark ? '#e2e8f0' : '#1f2937',
                              '&:hover': {
                                bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                              },
                              '&.Mui-selected': {
                                bgcolor: isDark ? 'rgba(129, 140, 248, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                                '&:hover': {
                                  bgcolor: isDark ? 'rgba(129, 140, 248, 0.3)' : 'rgba(102, 126, 234, 0.15)',
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>No session selected</em>
                      </MenuItem>
                      {selectedActivityType.Sessions.map((session) => (
                        <MenuItem key={session.ID} value={session.ID}>
                          {session.Code} - {session.Description}
                          {session.Time && ` (${new Date(session.Time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })})`}
                          {getNormalizedSessionDirection(session.Direction) === 'to' && ' - Direction: Hotel -> Destination'}
                          {getNormalizedSessionDirection(session.Direction) === 'from' && ' - Direction: Destination -> Hotel'}
                        </MenuItem>
                      ))}
                    </Select>
                    {selectedActivityType.Sessions.some((s) => getNormalizedSessionDirection(s.Direction) !== 'unknown') && (
                      <Typography variant="caption" sx={{
                        mt: 0.5,
                        ml: 1,
                        color: isDark ? 'rgba(148, 163, 184, 0.9)' : 'rgba(71, 85, 105, 0.9)',
                      }}>
                        Direction labels are informational for this activity; only shuttle reservations require separate outbound/return selection.
                      </Typography>
                    )}
                    {selectedActivityType.SessionMandatory && (
                      <Typography variant="caption" sx={{
                        mt: 0.5,
                        ml: 1,
                        color: isDark ? '#f87171' : '#ef4444',
                        fontWeight: 600
                      }}>
                        * Session is required for this activity
                      </Typography>
                    )}
                  </FormControl>
                )
              )}
              <TextField
                label="Event Name / Additional Details"
                fullWidth
                value={activityEventName}
                onChange={(e) => setActivityEventName(e.target.value)}
                placeholder="Optional event name or additional details"
                helperText="Optional"
                sx={textFieldSx}
                InputLabelProps={{
                  sx: {
                    color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{
            p: 3,
            gap: 1,
            bgcolor: 'transparent',
            borderTop: isDark ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
          }}>
            <Button
              onClick={() => setActivityModalOpen(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                                borderRadius: 16,
                px: 3,
                color: isDark ? '#cbd5e1' : '#6b7280',
                borderColor: isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(0, 0, 0, 0.12)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleActivityReservationSubmit}
              variant="contained"
              disabled={
                !activityTypeId
                || !activityDate
                || (selectedActivityType?.InternalType === 'ShuttleReservation'
                    ? (!shuttleOutboundSession && !shuttleReturnSession)
                    : (selectedActivityType?.SessionMandatory && !activitySession))
                || !!(activityDate && selectedActivityType?.ActiveDays?.length && !selectedActivityType.ActiveDays.includes(getDayNameForDate(activityDate)))
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                                borderRadius: 16,
                px: 3,
                background: isDark
                  ? 'linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: isDark
                  ? '0 4px 12px rgba(129, 140, 248, 0.4)'
                  : '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: isDark
                    ? 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)'
                    : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: isDark
                    ? '0 6px 16px rgba(129, 140, 248, 0.5)'
                    : '0 6px 16px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: isDark
                    ? 'linear-gradient(135deg, #475569 0%, #334155 100%)'
                    : 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
                  boxShadow: 'none',
                  color: isDark ? '#64748b' : undefined,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {selectedMessage?.type === MessageType.ACTIVITY_RESERVATION ? 'Update Reservation' : 'Create Reservation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Service Task Modal */}
        <Dialog
          open={serviceTaskModalOpen}
          onClose={() => setServiceTaskModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
                                borderRadius: 8,
              background: isDark
                ? 'rgba(15, 23, 42, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: isDark
                ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(148, 163, 184, 0.1) inset'
                : '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
              border: isDark
                ? '1px solid rgba(148, 163, 184, 0.2)'
                : '1px solid rgba(255, 255, 255, 0.18)',
            },
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            background: isDark
              ? 'linear-gradient(135deg, #818cf8 0%, #a78bfa 50%, #f472b6 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            {t('chat.newServiceTaskTitle')}
          </DialogTitle>
          <DialogContent sx={{
            bgcolor: 'transparent',
            '& .MuiFormHelperText-root': {
              color: isDark ? 'rgba(203, 213, 225, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            },
          }}>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel 
                  sx={{
                    color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                    '&.Mui-focused': {
                      color: isDark ? '#3b82f6' : '#3b82f6',
                    },
                  }}
                >
                  {t('chat.department')}
                </InputLabel>
                <Select
                  value={serviceTaskDepartment || ''}
                  label={t('chat.department')}
                  onChange={(e: SelectChangeEvent) => {
                    setServiceTaskDepartment(e.target.value);
                    setServiceTaskGroup(''); // Reset group when department changes
                    setServiceTaskType(''); // Reset type when department changes
                  }}
                  disabled={configLoading || !serviceTaskConfig?.Departments?.length}
                  inputProps={{
                    'aria-label': t('chat.department'),
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        maxHeight: 300,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('chat.loading')}
                    </MenuItem>
                  ) : serviceTaskConfig?.Departments?.length ? (
                    serviceTaskConfig.Departments
                      .filter(dept => !dept.Inactive)
                      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                      .map((dept) => (
                        <MenuItem key={dept.ID} value={dept.ID}>
                          {dept.Description}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>{t('chat.noDepartments')}</MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Group selection: always visible but disabled until department is chosen */}
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel 
                  sx={{
                    color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                    '&.Mui-focused': {
                      color: isDark ? '#3b82f6' : '#3b82f6',
                    },
                  }}
                >
                  {t('chat.group')}
                </InputLabel>
                <Select
                  value={serviceTaskGroup || ''}
                  label={t('chat.group')}
                  onChange={(e: SelectChangeEvent) => {
                    setServiceTaskGroup(e.target.value);
                    setServiceTaskType(''); // Reset type when group changes
                  }}
                  disabled={configLoading || !serviceTaskDepartment || !serviceTaskConfig?.Groups?.length}
                  inputProps={{
                    'aria-label': t('chat.group'),
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        maxHeight: 300,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('chat.loading')}
                    </MenuItem>
                  ) : !serviceTaskDepartment ? (
                    <MenuItem disabled>{t('chat.selectDepartmentFirst')}</MenuItem>
                  ) : serviceTaskConfig?.Groups?.filter(g => {
                    const deptId = g.Department?.ID || (g as any).DepartmentID || (g as any).DepartmentId || (g as any).departmentId;
                    return deptId === serviceTaskDepartment && !g.Inactive;
                  }).length ? (
                    serviceTaskConfig.Groups
                      .filter(g => {
                        const deptId = g.Department?.ID || (g as any).DepartmentID || (g as any).DepartmentId || (g as any).departmentId;
                        return deptId === serviceTaskDepartment && !g.Inactive;
                      })
                      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                      .map((group) => (
                        <MenuItem key={group.ID} value={group.ID}>
                          {group.Code} - {group.Description}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>{t('chat.noGroups')}</MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Task type selection: always visible but disabled until group is chosen */}
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel 
                  sx={{
                    color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                    '&.Mui-focused': {
                      color: isDark ? '#3b82f6' : '#3b82f6',
                    },
                  }}
                >
                  {t('chat.taskType')}
                </InputLabel>
                <Select
                  value={serviceTaskType || ''}
                  label={t('chat.taskType')}
                  onChange={(e: SelectChangeEvent) => setServiceTaskType(e.target.value)}
                  disabled={configLoading || !serviceTaskGroup || !serviceTaskConfig?.Types?.length}
                  inputProps={{
                    'aria-label': t('chat.taskType'),
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        maxHeight: 300,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('chat.loading')}
                    </MenuItem>
                  ) : !serviceTaskGroup ? (
                    <MenuItem disabled>{t('chat.selectGroupFirst')}</MenuItem>
                  ) : serviceTaskConfig?.Types?.filter(type => {
                    const groupId = type.Group?.ID || type.GroupID || (type as any).GroupId || (type as any).groupId;
                    return groupId === serviceTaskGroup && !type.Inactive;
                  }).length ? (
                    serviceTaskConfig.Types
                      .filter(type => {
                        const groupId = type.Group?.ID || type.GroupID || (type as any).GroupId || (type as any).groupId;
                        return groupId === serviceTaskGroup && !type.Inactive;
                      })
                      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                      .map((type) => (
                        <MenuItem key={type.ID} value={type.ID}>
                          {type.Code} - {type.Description}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>{t('chat.noTaskTypes')}</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel sx={{
                  color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  '&.Mui-focused': {
                    color: isDark ? '#3b82f6' : '#3b82f6',
                  },
                }}>{t('chat.localityType')}</InputLabel>
                <Select
                  value={serviceLocalityType}
                  label={t('chat.localityType')}
                  disabled={!!(chat?.room?.roomNumber && chat.room.roomNumber !== '000')}
                  onChange={(e: SelectChangeEvent) => {
                    setServiceLocalityType(e.target.value);
                    setServiceLocality(''); // Reset locality when type changes
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="Room">Room</MenuItem>
                  <MenuItem value="FunctionSpace">Function Space</MenuItem>
                  <MenuItem value="Location">Location</MenuItem>
                  <MenuItem value="PublicPlace">Public Place</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel sx={{
                  color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  '&.Mui-focused': {
                    color: isDark ? '#3b82f6' : '#3b82f6',
                  },
                }}>{t('chat.locality')}</InputLabel>
                <Select
                  value={serviceLocality}
                  label={t('chat.locality')}
                  onChange={(e: SelectChangeEvent) => setServiceLocality(e.target.value)}
                  disabled={!serviceLocalityType || configLoading || !serviceTaskConfig?.Localities?.length || (!!chat?.room?.roomNumber && chat.room.roomNumber !== '000' && !!serviceLocality)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('chat.loading')}
                    </MenuItem>
                  ) : serviceTaskConfig?.Localities
                    ?.filter(loc => loc.Type === serviceLocalityType)
                    .length ? (
                    serviceTaskConfig.Localities
                      .filter(loc => loc.Type === serviceLocalityType)
                      .map((locality) => (
                        <MenuItem key={locality.ID} value={locality.ID}>
                          {locality.Description}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>
                      {serviceLocalityType ? t('chat.noLocalities') : t('chat.selectLocalityTypeFirst')}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel sx={{
                  color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  '&.Mui-focused': {
                    color: isDark ? '#3b82f6' : '#3b82f6',
                  },
                }}>{t('chat.priority')}</InputLabel>
                <Select
                  value={servicePriority}
                  label={t('chat.priority')}
                  onChange={(e: SelectChangeEvent) => setServicePriority(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="Low">{t('chat.low')}</MenuItem>
                  <MenuItem value="Normal">{t('chat.normal')}</MenuItem>
                  <MenuItem value="High">{t('chat.high')}</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? theme.palette.text.primary : '#1f2937' }}>{t('chat.notes')}</Typography>
                  {selectedMessage && !selectedMessage.metadata && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleEnhanceServiceNote}
                      disabled={isEnhancingServiceNote || !selectedMessage.content}
                      startIcon={isEnhancingServiceNote ? <CircularProgress size={16} /> : null}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                                borderRadius: 16,
                        borderColor: isDark ? 'rgba(6, 182, 212, 0.4)' : 'rgba(6, 182, 212, 0.3)',
                        color: isDark ? '#22d3ee' : '#06b6d4',
                        '&:hover': {
                          borderColor: isDark ? '#22d3ee' : '#06b6d4',
                          backgroundColor: isDark ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)',
                        },
                      }}
                    >
                      {isEnhancingServiceNote ? t('chat.enhancing') : t('chat.aiEnhance')}
                    </Button>
                  )}
                </Box>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  value={serviceNotes}
                  onChange={(e) => setServiceNotes(e.target.value)}
                  placeholder={t('chat.notesPlaceholder')}
                  sx={textFieldSx}
                />
              </Box>
              <TextField
                label={t('chat.dueDate')}
                type="datetime-local"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  },
                }}
                value={serviceDueDate}
                onChange={(e) => setServiceDueDate(e.target.value)}
                sx={textFieldSx}
              />

              {/* Image selector - show recent images from this chat */}
              <FormControl fullWidth sx={textFieldSx}>
                <InputLabel sx={{
                  color: isDark ? 'rgba(203, 213, 225, 0.7)' : undefined,
                  '&.Mui-focused': {
                    color: isDark ? '#3b82f6' : '#3b82f6',
                  },
                }}>{t('chat.attachImageOptional')}</InputLabel>
                <Select
                  value={selectedTaskImage?.id || ''}
                  onChange={(e) => {
                    const imageMsg = messages.find(m => m.id === e.target.value);
                    setSelectedTaskImage(imageMsg || null);
                  }}
                  label={t('chat.attachImageOptional')}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 6,
                        mt: 1,
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                        boxShadow: isDark
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : 'none',
                        '& .MuiMenuItem-root': {
                          color: isDark ? '#e2e8f0' : '#1f2937',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t('chat.noImage')}</em>
                  </MenuItem>
                  {messages
                    .filter(m => m.type === MessageType.IMAGE)
                    .reverse() // Show most recent first
                    .slice(0, 10) // Limit to last 10 images
                    .map(msg => (
                      <MenuItem key={msg.id} value={msg.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 16,
                            p: 0.5,
                            display: 'inline-block',
                          }}>
                            <img
                              src={msg.content}
                              alt="Thumbnail"
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 16,
                                border: 'none',
                                display: 'block',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#6b7280', fontWeight: 500 }}>
                            {new Date(msg.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  }
                </Select>
                {selectedTaskImage && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                                borderRadius: 16,
                    background: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(10px)',
                  }}>
                    <Typography variant="caption" sx={{ color: isDark ? '#cbd5f5' : '#6b7280', fontWeight: 600, display: 'block', mb: 1 }}>
                      {t('chat.selectedImagePreview')}
                    </Typography>
                    <Box sx={{
                      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 16,
                      p: 1,
                      display: 'inline-block',
                      mt: 1,
                    }}>
                      <img
                        src={selectedTaskImage.content}
                        alt="Selected"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 150,
                          borderRadius: 16,
                          display: 'block',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{
            p: 3,
            gap: 1,
            bgcolor: 'transparent',
            borderTop: isDark ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
          }}>
            <Button
              onClick={() => setServiceTaskModalOpen(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                                borderRadius: 16,
                px: 3,
                color: isDark ? '#cbd5e1' : '#6b7280',
                borderColor: isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(0, 0, 0, 0.12)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {t('login.cancel')}
            </Button>
            <Button
              onClick={handleServiceTaskSubmit}
              variant="contained"
              disabled={!serviceTaskDepartment || !serviceTaskGroup || !serviceTaskType || !serviceLocality}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                                borderRadius: 16,
                px: 3,
                background: isDark
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                boxShadow: isDark
                  ? '0 4px 12px rgba(59, 130, 246, 0.4)'
                  : '0 4px 12px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: isDark
                    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  boxShadow: isDark
                    ? '0 6px 16px rgba(59, 130, 246, 0.5)'
                    : '0 6px 16px rgba(59, 130, 246, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: isDark
                    ? 'linear-gradient(135deg, #475569 0%, #334155 100%)'
                    : 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
                  boxShadow: 'none',
                  color: isDark ? '#64748b' : undefined,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {t('chat.createTask')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Preview Modal */}
        <Dialog
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
                                borderRadius: 16,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
              border: '1px solid rgba(255, 255, 255, 0.18)',
            },
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Image Preview
            <IconButton
              aria-label="close"
              onClick={() => setImagePreviewOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: '#6b7280',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: isDark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 16,
              p: 2,
            }}>
              <img
                src={previewImageUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                }}
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* Info Dialog */}
        <Dialog
          open={infoDialog.open}
          onClose={closeInfoDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
                                borderRadius: 16,
              textAlign: 'center',
              p: 2,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>{infoDialog.title || 'Bilgilendirme'}</DialogTitle>
          <DialogContent>
            <Alert severity={infoDialog.severity} sx={{ borderRadius: 6, fontSize: '0.95rem' }}>
              <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                {infoDialog.message}
              </Box>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button
              variant="contained"
              onClick={closeInfoDialog}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 6,
                px: 4,
              }}
            >
              Tamam
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogState.open}
          onClose={() => handleConfirmDialogClose(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
                                borderRadius: 16,
              p: 2,
              bgcolor: isDark ? theme.palette.background.paper : undefined,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: isDark ? theme.palette.text.primary : undefined }}>{confirmDialogState.title || 'Onay'}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: isDark ? theme.palette.text.primary : '#374151' }}>
              {confirmDialogState.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
            <Button
              onClick={() => handleConfirmDialogClose(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 6,
                px: 3,
              }}
            >
              {confirmDialogState.cancelLabel || 'İptal'}
            </Button>
            <Button
              variant="contained"
              onClick={() => handleConfirmDialogClose(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 6,
                px: 3,
              }}
            >
              {confirmDialogState.confirmLabel || 'Onayla'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default ChatPage;